import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Menu,
  MenuItem,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { apiService } from '../../services/apiService';
import { CELL_HOVER_SPOTLIGHT, BLUE_TOOLTIP_PROPS, BLUE_TOOLTIP_HINT_PROPS } from '../common/tableStyles';

const RecommendationTab = ({
  conversion,
  recommendedConversion,
  distributionSchedule,
  inputInfo,
  userInfo,
  runId,
  parts,
  conversions,
  isRecommended,
  hasData,
  onViewAllConversions,
  onAddAssumptions,
  onViewRecommended,
  autoEmailReport,
  setAutoEmailReport,
  onToggleTaxSource,
  onToggleUseIRSBrackets,
  onNavigateToChart,
  selectedYearlyView,
  setSelectedYearlyView,
  showComparison,
  setShowComparison
}) => {
  const [taxSourceConfirmOpen, setTaxSourceConfirmOpen] = useState(false);
  const [localTaxSource, setLocalTaxSource] = useState(inputInfo?.tax_payment_source || 'O');
  useEffect(() => { setLocalTaxSource(inputInfo?.tax_payment_source || 'O'); }, [inputInfo?.tax_payment_source]);
  const pendingTaxSource = localTaxSource === 'I' ? 'O' : 'I';
  // 2026-04-18: SS torpedo bracket-sizing toggle
  const [useIRSBracketsConfirmOpen, setUseIRSBracketsConfirmOpen] = useState(false);
  const [localUseIRSBrackets, setLocalUseIRSBrackets] = useState(inputInfo?.use_irs_brackets || 'Y');
  useEffect(() => { setLocalUseIRSBrackets(inputInfo?.use_irs_brackets || 'Y'); }, [inputInfo?.use_irs_brackets]);
  const pendingUseIRSBrackets = localUseIRSBrackets === 'Y' ? 'N' : 'Y';
  const [yearlyMenuAnchor, setYearlyMenuAnchor] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // (Auto-email of the welcome report moved to App.js so it fires on the first run regardless
  // of which tab is active, instead of only when this tab mounts.)

  const handleToggleComparison = async () => {
    if (!showComparison && comparisonData.length === 0 && runId && conversion?.conv_group_num) {
      setComparisonLoading(true);
      try {
        const data = await apiService.getRetireYearDataComparison(runId, conversion.conv_group_num);
        setComparisonData(data);
      } catch (error) {
        console.error('Failed to fetch comparison data:', error);
      } finally {
        setComparisonLoading(false);
      }
    }
    setShowComparison(!showComparison);
  };

  // Eagerly fetch comparison data when conversion changes
  useEffect(() => {
    setComparisonData([]);
    if (runId && conversion?.conv_group_num) {
      setComparisonLoading(true);
      apiService.getRetireYearDataComparison(runId, conversion.conv_group_num)
        .then(data => setComparisonData(data))
        .catch(error => console.error('Failed to fetch comparison data:', error))
        .finally(() => setComparisonLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, conversion?.conv_group_num]);

  // Calculate totals for display
  const totalPreConversion = comparisonData.reduce((sum, row) => sum + row.atcf_group_0, 0);
  const totalPostConversion = comparisonData.reduce((sum, row) => sum + row.atcf_group_n, 0);
  const totalChange = comparisonData.reduce((sum, row) => sum + row.atcf_change, 0);
  const totalTradDist = comparisonData.reduce((sum, row) => sum + (row.trad_dist_n || 0), 0);
  const totalRothDist = comparisonData.reduce((sum, row) => sum + (row.roth_dist_n || 0), 0);
  const totalSocSec = comparisonData.reduce((sum, row) => sum + (row.soc_sec_n || 0), 0);
  const totalIrmaaSavings = comparisonData.reduce((sum, row) => sum + (row.irmaa_savings || 0), 0);
  const irmaaConvYearCost = conversion?.irmaa_surcharge_cost || 0;
  const irmaaConvYearCostPv = inputInfo?.dist_return_assum ? irmaaConvYearCost / Math.pow(1 + inputInfo.dist_return_assum, 2) : irmaaConvYearCost;
  const irmaaFutureSavings = Math.max(0, (conversion?.irmaa_savings || 0) - irmaaConvYearCost);
  const irmaaFutureSavingsPv = Math.max(0, (conversion?.irmaa_savings_pv || 0) - irmaaConvYearCostPv);
  const totalOtherInc = Math.round(comparisonData.reduce((sum, row) => sum + (row.other_taxable_inc || 0), 0)) || 0;
  const totalFedTax = comparisonData.reduce((sum, row) => sum + (row.fed_tax_n || 0), 0);
  const yearlyDrag = conversion?.tax_drag_rmd_yearly || [];
  const yts = distributionSchedule?.years_to_start_year || 0;
  const totalTaxDrag = conversion?.tax_drag_rmd_fv || 0;

  // 2026-04-20: Booleans gating the two SS Torpedo accordion sections
  const hasConversionTorpedo = parts && parts.some(p =>
    p.conv_group_num <= (conversion?.conv_group_num || 0) && (p.conv_ss_taxable_delta || 0) > 0
  );
  const hasDistributionTorpedo = comparisonData && comparisonData.some(r =>
    (r.soc_sec_n || 0) > 0 && (
      ((r.pct_ss_taxed_opt_0 || 0) > 0 && (r.pct_ss_taxed_opt_0 || 0) < 0.85)
      || ((r.pct_ss_taxed_opt_n || 0) > 0 && (r.pct_ss_taxed_opt_n || 0) < 0.85)
    )
  );

  // HC FV and Spread FV from backend values
  const isOutsidePayment = inputInfo?.tax_payment_source !== 'I';
  const convSpreadFv = conversion?.spread_fv_tw || 0;
  // 2026-09-27: engine-computed alpha totals — the shelter is gated on funding source there.
  const convAlphaPvTotal = conversion?.conversion_alpha_pv || 0;
  const convAlphaFvTotal = conversion?.conversion_alpha_fv || 0;
  // tax_drag_cont_pv/fv carry the SRC shelter on EVERY run, including Inside, where it is
  // the counterfactual value of funding from Outside instead. Tables here show what was
  // actually earned, so they read these; only the funding caption reads the raw field.
  const srcShelterEarnedPv = (row) =>
    (inputInfo?.tax_payment_source === 'O' ? (row?.tax_drag_cont_pv || 0) : 0);
  const srcShelterEarnedFv = (row) =>
    (inputInfo?.tax_payment_source === 'O' ? (row?.tax_drag_cont_fv || 0) : 0);
  const hcFvTotal = conversion ? (conversion.total_after_tax_dist_chg_amt || 0) - (conversion.irmaa_savings || 0) - (conversion.tax_drag_rmd_fv || 0) : 0;
  const convHcFv = isOutsidePayment ? hcFvTotal - convSpreadFv : 0;
  // Get the marginal bracket's spread (the last bracket in the conversion)
  const marginalPart = parts?.find(p => p.conv_group_num === conversion?.conv_group_num);
  const portfolioReturn = inputInfo?.dist_return_assum || 0;
  const marginalSpreadPositive = marginalPart ? ((marginalPart.spread_pv_tw || 0) + (marginalPart.irmaa_savings_pv || 0) + (marginalPart.tax_drag_rmd_pv || 0)) >= 0 : ((conversion?.spread_pv_tw || 0) + (conversion?.irmaa_savings_pv || 0) + (conversion?.tax_drag_rmd_pv || 0)) >= 0;

  const formatDollar = (value) => {
    if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  // No data state
  if (!hasData || !conversion) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" gutterBottom>
          Your Recommended Conversion
        </Typography>
        <Box sx={{ textAlign: 'left', display: 'inline-block', mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Run your first calculation to see:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <Typography component="li" variant="body1">Your optimal conversion amount and which brackets to fill</Typography>
            <Typography component="li" variant="body1">Annual return (IRR) earned on your conversion tax payment</Typography>
            <Typography component="li" variant="body1">Breakdown: how much comes from tax-rate arbitrage vs. the synthetic contribution</Typography>
          </Box>
        </Box>
        {onAddAssumptions && (
          <Button variant="contained" color="primary" onClick={onAddAssumptions}>
            Add/Chg Assumptions
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* Combined Summary + Components box */}
      <Box sx={{ border: '2px solid #1976d2', borderRadius: 1, mb: 1 }}>
      {/* Yellow summary section */}
      <Box sx={{ px: 3, pt: 1, pb: 0.5, backgroundColor: '#fffde7', borderBottom: showComparison ? '1px solid #ccc' : 'none' }}>
        {/* Unified 6-column grid: $/label cluster | tax-rate cluster | return cluster. 3 rows. Bottom row of each cluster is the "headline alpha": Wealth Change | Spread | Conversion alpha */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr auto auto auto auto', columnGap: 1.5, rowGap: 0, alignItems: 'baseline', mb: 0 }}>
          {/* Row 1 */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, textAlign: 'right' }}>${conversion.conv_amt?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2, color: 'text.secondary' }}>
            <Box component="span" sx={{ color: 'text.primary' }}>CONVERSION</Box> - Fills through {(conversion.tax_rate_bucket * 100).toFixed(0)}% bracket,{' '}
            {isRecommended
              /* 2026-10-07: was conv_irr >= portfolioReturn, the hurdle 055035a retired. That IRR omits the SRC
                 shelter (NPV of its cash flows = conversion_alpha_pv - tax_drag_cont_pv, verified to the cent), so
                 it printed BEST AVAILABLE for Outside conversions the engine recommends on positive total alpha.
                 No-op under Inside, where the conv_irr sentinel already encodes exactly this test. */
              ? <Box component="span" sx={{ fontWeight: 'bold', color: (conversion.conversion_alpha_pv || 0) > 0 ? 'success.main' : 'error.main' }}>{(conversion.conversion_alpha_pv || 0) > 0 ? 'RECOMMENDED!' : 'BEST AVAILABLE'}</Box>
              : (onViewRecommended ? (
                  <Tooltip arrow {...BLUE_TOOLTIP_HINT_PROPS} title="Show recommended conversion">
                    <Box component="span" onClick={onViewRecommended} sx={{ fontWeight: 'bold', color: 'error.main', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px', '&:hover': { color: 'error.dark' } }}>
                      {conversion.tax_rate_bucket < recommendedConversion?.tax_rate_bucket ? 'Convert More?' : 'Convert Less?'}
                    </Box>
                  </Tooltip>
                ) : (
                  <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {conversion.tax_rate_bucket < recommendedConversion?.tax_rate_bucket ? 'Convert More?' : 'Convert Less?'}
                  </Box>
                ))}
          </Typography>
          <Typography variant="body2" sx={{ ml: 4, textAlign: 'right', lineHeight: 1.2, fontWeight: 'bold', color: 'text.secondary' }}>Distribution Tax Rate:</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.2, textAlign: 'right' }}>{(conversion.conv_dist_tax_rate_tw * 100).toFixed(1)}%</Typography>
          <Typography variant="body2" sx={{ ml: 4, textAlign: 'right', lineHeight: 1.2, fontWeight: 'bold', color: 'text.secondary' }}>Conversion Return:</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.2, textAlign: 'right' }}>{(conversion.conv_irr * 100).toFixed(2)}%</Typography>

          {/* Row 2 */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, textAlign: 'right' }}>${inputInfo?.tax_payment_source === 'I' ? '0' : conversion.conv_tax?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>Conversion Tax</Typography>
            {/* 2026-09-12: $0 above is out-of-pocket; this is what actually left the Traditional
                account, matching the conv_tax figure on the CONVERSIONS tab. */}
            {inputInfo?.tax_payment_source === 'I' && (conversion.conv_tax || 0) > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', lineHeight: 1.2, whiteSpace: 'nowrap' }}>(Inside payment ${Math.round(conversion.conv_tax).toLocaleString('en-US')})</Typography>
            )}
            {(conversion.conv_ss_tax_delta || 0) > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', lineHeight: 1.2, whiteSpace: 'nowrap' }}>(<Box component="span" sx={{ color: 'error.main' }}>${Math.round(conversion.conv_ss_tax_delta).toLocaleString()}</Box> from <Box component="span" sx={{ color: '#000' }}>${Math.round(conversion.conv_ss_taxable_delta).toLocaleString()}</Box> taxable SS)</Typography>
            )}
          </Box>
          <Typography variant="body2" sx={{ ml: 4, textAlign: 'right', lineHeight: 1.2, fontWeight: 'bold', color: 'text.secondary' }}>Conversion Tax Rate:</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.2, textAlign: 'right', borderBottom: '1px solid' }}>{(conversion.conv_tax_rate * 100).toFixed(1)}%</Typography>
          <Typography variant="body2" sx={{ ml: 4, textAlign: 'right', lineHeight: 1.2, fontWeight: 'bold', color: 'text.secondary' }}>Portfolio Return:</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.2, textAlign: 'right', borderBottom: '1px solid' }}>{inputInfo?.dist_return_assum ? (inputInfo.dist_return_assum * 100).toFixed(2) : '5.00'}%</Typography>

          {/* Row 3: left side blank, right side has Spread + Conversion alpha (the "headline difference" of each rate cluster) */}
          <Box />
          <Box />
          <Typography variant="body2" sx={{ ml: 4, fontWeight: 'bold', textAlign: 'right', lineHeight: 1.2 }}>Spread:</Typography>
          {(() => {
            const spread = (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate) * 100;
            const spreadStr = (spread >= 0 ? '+' : '−') + Math.abs(spread).toFixed(1) + '%';
            return (
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, textAlign: 'right', color: spread > 0 ? 'success.main' : spread < 0 ? 'error.main' : 'text.primary' }}>{spreadStr}</Typography>
            );
          })()}
          <Typography variant="body2" sx={{ ml: 4, fontWeight: 'bold', textAlign: 'right', lineHeight: 1.2 }}>Conversion alpha:</Typography>
          {(() => {
            const isInside = inputInfo?.tax_payment_source === 'I';
            if (isInside) {
              const pvWindfall = (conversion.spread_pv_tw || 0) + (conversion.irmaa_savings_pv || 0) + (conversion.tax_drag_rmd_pv || 0);
              const rounded = Math.round(pvWindfall);
              if (rounded > 0) {
                return <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, textAlign: 'right', color: 'success.main' }}>+100%</Typography>;
              }
              if (rounded < 0) {
                return <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, textAlign: 'right', color: 'error.main' }}>−100%</Typography>;
              }
              return <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, textAlign: 'right' }}>0%</Typography>;
            }
            const alpha = (conversion.conv_irr - (inputInfo?.dist_return_assum || 0)) * 100;
            const alphaStr = (alpha >= 0 ? '+' : '−') + Math.abs(alpha).toFixed(2) + '%';
            return (
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, textAlign: 'right', color: alpha > 0 ? 'success.main' : alpha < 0 ? 'error.main' : 'text.primary' }}>{alphaStr}</Typography>
            );
          })()}

          {/* Row 4: left side has Wealth Change (the dollar "alpha"), right side blank */}
          {(() => {
            const wealthChange = Math.round((conversion.spread_pv_tw || 0) + (conversion.irmaa_savings_pv || 0) + (conversion.tax_drag_rmd_pv || 0));
            const wealthStr = (wealthChange >= 0 ? '+$' : '−$') + Math.abs(wealthChange).toLocaleString();
            return (
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, textAlign: 'right', color: wealthChange >= 0 ? 'success.main' : 'error.main' }}>{wealthStr}</Typography>
            );
          })()}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>Immediate Wealth Change from Converting ${conversion.conv_amt?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
          </Box>
          <Box />
          <Box />
          <Box />
          <Box />
          {/* 2026-10-08: caption moved to its own grid row so the Inside IRR sits on the SAME line, with the rate still
              in column 6 under the other returns. Spans columns 2-5 to keep the caption's original indent. */}
          <Box />
          <Box sx={{ gridColumn: 'span 4', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', columnGap: 2 }}>
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.2 }}>
              same whether you pay tax Outside or Inside
              {/* Deliberately the RAW field, not the gated helper: on an Inside run this is
                  the counterfactual, which is exactly what makes the sentence worth showing. */}
              {Math.round(conversion.tax_drag_cont_pv || 0) !== 0 && (
                <Box component="span" sx={{ fontWeight: 'bold', fontStyle: 'normal', color: 'text.primary' }}>
                  {` — Outside funding ${inputInfo?.tax_payment_source === 'O' ? 'adds' : 'would add'} $${Math.round(conversion.tax_drag_cont_pv).toLocaleString()}`}
                </Box>
              )}
            </Typography>
            {/* Inside only: "Conversion Return" above is the no-outlay infinite-return cap, so this is the finite one. */}
            {inputInfo?.tax_payment_source === 'I' && conversion.conv_irr_outside != null && (
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                ⟶ IRR if your external funds pay the tax
              </Typography>
            )}
          </Box>
          {inputInfo?.tax_payment_source === 'I' && conversion.conv_irr_outside != null
            ? <Typography variant="body2" sx={{ lineHeight: 1.2, textAlign: 'right', fontWeight: 'bold' }}>{(conversion.conv_irr_outside * 100).toFixed(2)}%</Typography>
            : <Box />}
        </Box>
      </Box>


      {/* CONVERSION COMPONENTS table */}
      <Box sx={{ borderTop: '2px solid #1976d2', position: 'relative' }}>
        {/* Year-by-Year Detail Reports button absolutely positioned, overlays gray section top (PV/FV row when collapsed) */}
        <Box sx={{ position: 'absolute', top: '1px', left: '50%', transform: 'translateX(-50%)', zIndex: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => setYearlyMenuAnchor(e.currentTarget)}
            disabled={comparisonLoading || !runId}
            sx={{ fontWeight: 'bold' }}
            endIcon={<ExpandMoreIcon />}
          >
            {showComparison
              ? (selectedYearlyView === 'income_tax' ? 'Income and Tax' : selectedYearlyView === 'medicare' ? 'Medicare Premiums' : selectedYearlyView === 'ss_tax_reduction' ? 'Social Security Tax Reduction' : 'IRR Cash Flows and Conversion Alpha')
              : 'Year-by-Year Detail Reports'}
          </Button>
          <Menu
            anchorEl={yearlyMenuAnchor}
            open={Boolean(yearlyMenuAnchor)}
            onClose={() => setYearlyMenuAnchor(null)}
          >
            <MenuItem onClick={() => {
              setSelectedYearlyView('income_tax');
              setYearlyMenuAnchor(null);
              if (!showComparison) handleToggleComparison();
            }} selected={selectedYearlyView === 'income_tax' && showComparison}>
              Income and Tax
            </MenuItem>
            <MenuItem onClick={() => {
              setSelectedYearlyView('medicare');
              setYearlyMenuAnchor(null);
              if (!showComparison) handleToggleComparison();
            }} selected={selectedYearlyView === 'medicare' && showComparison}>
              Medicare Premiums
            </MenuItem>
            <MenuItem onClick={() => {
              setSelectedYearlyView('irr_decomp');
              setYearlyMenuAnchor(null);
              if (!showComparison) handleToggleComparison();
            }} selected={selectedYearlyView === 'irr_decomp' && showComparison}>
              IRR Cash Flows and Conversion Alpha
            </MenuItem>
            <MenuItem onClick={() => {
              setSelectedYearlyView('ss_tax_reduction');
              setYearlyMenuAnchor(null);
              if (!showComparison) handleToggleComparison();
            }} selected={selectedYearlyView === 'ss_tax_reduction' && showComparison}>
              Social Security Tax Reduction
            </MenuItem>
            {showComparison && (
              <MenuItem onClick={() => {
                setYearlyMenuAnchor(null);
                setShowComparison(false);
              }}>
                Hide Details
              </MenuItem>
            )}
          </Menu>
        </Box>
        {/* Year-by-Year Comparison Table - expands below the summary. Inner Box pt:'52px' reserves space for the absolute-positioned button so titles aren't hidden when expanded. */}
        <Collapse in={showComparison} timeout="auto" unmountOnExit>
          <Box sx={{ pt: '40px', backgroundColor: '#fff' }}>
          {selectedYearlyView === 'income_tax' && (
          <Box sx={{ mb: 0 }}>
            <Typography variant="subtitle1" sx={{ textAlign: 'center', mb: 0 }}>
              After-Tax Cash Flow (ATCF) - Before and After Conversion
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.25 }, '& tbody td:hover': CELL_HOVER_SPOTLIGHT }}>
                <TableHead sx={{ '& .MuiTableCell-root': { py: 0, lineHeight: 1.1, borderBottom: '2px solid #000' } }}>
                  <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                    <TableCell rowSpan={2}>Year</TableCell>
                    <TableCell rowSpan={2}>Age</TableCell>
                    <TableCell align="right" rowSpan={2}>ATCF Pre-<br />Conversion</TableCell>
                    <TableCell align="center" colSpan={13} sx={{ fontWeight: 'bold' }}>
                      {inputInfo?.distribution_method === 'R' ? 'Post-Conversion - RMD based Distributions' : 'Post-Conversion - Fixed Annuity Distributions'}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                    <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', borderLeft: '2px solid #1976d2', borderRight: '2px solid #1976d2' }}>ATCF Chg</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', borderRight: '2px dashed #1976d2' }}>Hidden Cont</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Rate Arb</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Tax-Drag</TableCell>
                    <Tooltip title="IRMAA Δ is Medicare surcharge savings from lower post-conversion MAGI. Unlike Rate Arb and Hidden Cont (whose amounts are already reflected in the Roth distribution and federal tax columns), IRMAA Δ increases ATCF and is also an independent income source." arrow>
                    <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', borderRight: '2px solid #1976d2', cursor: 'help' }}>IRMAA Δ</TableCell>
                    </Tooltip>
                    <TableCell align="right">Trad-Dist</TableCell>
                    <TableCell align="right">Roth-Dist</TableCell>
                    <TableCell align="right">Soc-Sec</TableCell>
                    <TableCell align="right">Other</TableCell>
                    <TableCell align="right">Fed-Tax</TableCell>
                    <TableCell align="right">ATCF</TableCell>
                    <TableCell align="right">Traditional<br />Savings</TableCell>
                    <TableCell align="right">Roth<br />Savings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonLoading ? (
                    <TableRow>
                      <TableCell colSpan={16} align="center">Loading...</TableCell>
                    </TableRow>
                  ) : comparisonData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={16} align="center">No comparison data available</TableCell>
                    </TableRow>
                  ) : (
                    comparisonData
                      .map((row, i) => (
                        <TableRow key={`comp-${row.year}-${row.age}`}>
                          <TableCell>{new Date(row.year).getFullYear()}</TableCell>
                          <TableCell>{row.age}</TableCell>
                          <TableCell align="right">
                            ${row.atcf_group_0.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          {(() => {
                            const ex = row.atcf_change || 0;  // 2026-10-05: atcf_change no longer carries IRMAA, so no subtraction
                            const hcYr = ex * (convHcFv / (hcFvTotal || 1));
                            const spreadYr = ex * (convSpreadFv / (hcFvTotal || 1));
                            const dragYr = yearlyDrag[i + yts] || 0;
                            return (<>
                              <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', borderRight: '2px solid #1976d2', color: row.atcf_change >= 0 ? 'success.main' : 'error.main' }}>
                                ${row.atcf_change.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                              <TableCell align="right" sx={{ borderRight: '2px dashed #1976d2', color: hcYr >= 0 ? 'success.main' : 'error.main' }}>
                                ${(Math.round(hcYr) + 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                              <TableCell align="right" sx={{ color: spreadYr >= 0 ? 'success.main' : 'error.main' }}>
                                ${(Math.round(spreadYr) + 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                              <TableCell align="right" sx={{ color: dragYr >= 0 ? 'success.main' : 'error.main' }}>
                                ${(Math.round(dragYr) + 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                              <TableCell align="right" sx={{ borderRight: '2px solid #1976d2', color: (row.irmaa_savings || 0) >= 0 ? 'success.main' : 'error.main' }}>
                                ${Math.round(row.irmaa_savings || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                            </>);
                          })()}
                          <TableCell align="right">
                            ${row.trad_dist_n?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${row.roth_dist_n?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${row.soc_sec_n?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${Math.round(row.other_taxable_inc || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${row.fed_tax_n?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${row.atcf_group_n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${row.trad_savings_n?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${row.roth_savings_n?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                  {comparisonData.length > 0 && (
                    <TableRow sx={{ '& .MuiTableCell-root': { borderTop: '2px solid #000', fontWeight: 'bold' } }}>
                      <TableCell colSpan={2}>Total</TableCell>
                      <TableCell align="right">${totalPreConversion.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', borderLeft: '2px solid #1976d2', borderRight: '2px solid #1976d2', color: totalChange >= 0 ? '#2e7d32' : '#d32f2f' }}>
                        ${totalChange.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', borderRight: '2px dashed #1976d2', color: '#2e7d32' }}>
                        ${Math.round(convHcFv).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', color: convSpreadFv >= 0 ? '#2e7d32' : '#d32f2f' }}>
                        ${Math.round(convSpreadFv).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', color: totalTaxDrag >= 0 ? '#2e7d32' : '#d32f2f' }}>
                        ${Math.round(totalTaxDrag).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', borderRight: '2px solid #1976d2', color: totalIrmaaSavings >= 0 ? '#2e7d32' : '#d32f2f' }}>
                        ${Math.round(totalIrmaaSavings).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="right">${totalTradDist.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                      <TableCell align="right">${totalRothDist.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                      <TableCell align="right">${totalSocSec.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                      <TableCell align="right">${totalOtherInc.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                      <TableCell align="right">${totalFedTax.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                      <TableCell align="right">${totalPostConversion.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Savings & Distributions Line Chart */}
            {comparisonData.length > 0 && (() => {
              const currentYear = new Date().getFullYear();
              const firstDataYear = new Date(comparisonData[0].year).getFullYear();
              const firstAge = comparisonData[0].age;
              const growthRate = inputInfo?.dist_return_assum || 0.05;
              const convAmt = conversion?.conv_amt || 0;
              const todayTrad = (userInfo?.trad_savings || 0) - convAmt;
              const todayRoth = (userInfo?.roth_savings || 0) + convAmt;

              const preRetirementData = [];
              for (let y = currentYear; y < firstDataYear; y++) {
                const yearsFromNow = y - currentYear;
                const growth = Math.pow(1 + growthRate, yearsFromNow);
                preRetirementData.push({
                  year: y,
                  age: firstAge - (firstDataYear - y),
                  trad_savings_n: todayTrad * growth,
                  roth_savings_n: todayRoth * growth,
                  trad_dist_n: null,
                  roth_dist_n: null,
                });
              }

              const chartData = [
                ...preRetirementData,
                ...comparisonData.map(row => ({
                  year: new Date(row.year).getFullYear(),
                  age: row.age,
                  trad_savings_n: row.trad_savings_n || 0,
                  roth_savings_n: row.roth_savings_n || 0,
                  trad_dist_n: row.trad_dist_n || null,
                  roth_dist_n: row.roth_dist_n || null,
                  taxable_income_n: row.taxable_income_n ?? null,
                  trad_mtr_n: row.trad_mtr_n ?? null,
                  trad_mtr_adj_n: row.trad_mtr_adj_n ?? null,
                  trad_dist_opt_tax_rate_n: row.trad_dist_opt_tax_rate_n ?? null,
                }))
              ];

              return (
              <>
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle1" sx={{ textAlign: 'center', mb: 0 }}>
                  Post-Conversion Savings & Distributions ({inputInfo?.distribution_method === 'R' ? 'RMD Only + 10yr beneficiary period' : 'Fixed Annuity'})
                </Typography>
                <Box sx={{ height: 378, overflow: 'hidden' }}>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={({ x, y, payload }) => {
                      const dataPoint = chartData.find(d => d.year === payload.value);
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text x={0} y={0} dy={12} textAnchor="middle" fontSize={12}>{payload.value}</text>
                          <text x={0} y={0} dy={26} textAnchor="middle" fontSize={10} fill="#666">({dataPoint?.age})</text>
                        </g>
                      );
                    }} height={60} />
                    <YAxis yAxisId="left" tickFormatter={formatDollar} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={formatDollar} />
                    <RechartsTooltip formatter={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} />
                    <Legend verticalAlign="bottom" wrapperStyle={{ bottom: 22 }} content={({ payload }) => (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, paddingTop: 0 }}>
                        {payload.map((entry) => (
                          <span key={entry.dataKey} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <svg width="30" height="10">
                              <line x1="0" y1="5" x2="30" y2="5"
                                stroke={entry.color}
                                strokeWidth={entry.payload.strokeWidth || 1}
                                strokeDasharray={entry.payload.strokeDasharray || 'none'} />
                            </svg>
                            {entry.value}
                          </span>
                        ))}
                      </div>
                    )} />
                    <Line yAxisId="left" type="monotone" dataKey="trad_savings_n" name="Traditional Savings" stroke="#1976d2" dot={false} />
                    <Line yAxisId="left" type="monotone" dataKey="roth_savings_n" name="Roth Savings" stroke="#2e7d32" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="trad_dist_n" name="Traditional Distributions" stroke="#1976d2" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="roth_dist_n" name="Roth Distributions" stroke="#2e7d32" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
                </Box>
              </Box>

              {/* Tax Rates & Income Chart - commented out for now
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ textAlign: 'center', mb: 0 }}>
                  Tax Rates & Income Over Retirement
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData.filter(d => d.year >= new Date(comparisonData[0].year).getFullYear())}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={({ x, y, payload }) => {
                      const dataPoint = chartData.find(d => d.year === payload.value);
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text x={0} y={0} dy={12} textAnchor="middle" fontSize={12}>{payload.value}</text>
                          <text x={0} y={0} dy={26} textAnchor="middle" fontSize={10} fill="#666">({dataPoint?.age})</text>
                        </g>
                      );
                    }} height={50} />
                    <YAxis yAxisId="left" tickFormatter={(val) => `${(val * 100).toFixed(0)}%`} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={formatDollar} />
                    <RechartsTooltip formatter={(value, name) => {
                      if (name.includes('Rate') || name.includes('MTR')) return `${(value * 100).toFixed(1)}%`;
                      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                    }} />
                    <Legend verticalAlign="bottom" content={({ payload }) => (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, paddingTop: 8 }}>
                        {payload.map((entry) => (
                          <span key={entry.dataKey} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <svg width="30" height="10">
                              <line x1="0" y1="5" x2="30" y2="5"
                                stroke={entry.color}
                                strokeWidth={entry.payload.strokeWidth || 1}
                                strokeDasharray={entry.payload.strokeDasharray || 'none'} />
                            </svg>
                            {entry.value}
                          </span>
                        ))}
                      </div>
                    )} />
                    <Line yAxisId="left" type="monotone" dataKey="trad_mtr_n" name="Marginal Tax Rate (MTR)" stroke="#d32f2f" dot={false} />
                    <Line yAxisId="left" type="monotone" dataKey="trad_mtr_adj_n" name="Adjusted MTR" stroke="#f57c00" dot={false} />
                    <Line yAxisId="left" type="monotone" dataKey="trad_dist_opt_tax_rate_n" name="Distribution Tax Rate" stroke="#7b1fa2" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="trad_dist_n" name="Traditional Distribution" stroke="#1976d2" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="taxable_income_n" name="Taxable Income" stroke="#2e7d32" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              */}
              </>
              );
            })()}
          </Box>
          )}

          {selectedYearlyView === 'medicare' && (
          <Box sx={{ mb: 0 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', mb: 0 }}>
              Medicare IRMAA Premiums - Before and After Conversion
            </Typography>
            {(() => {
              const medicareData = inputInfo?.distribution_method === 'R' && inputInfo?.life_years
                ? comparisonData.slice(0, inputInfo.life_years)
                : comparisonData;
              const convYearMagi0 = (inputInfo?.taxable_inc_when_convert || 0);
              const convYearMagiN = (inputInfo?.taxable_inc_when_convert || 0) + (conversion?.conv_amt || 0) + (conversion?.conv_ss_taxable_delta || 0);
              const preDistRow = yts >= 2 && irmaaConvYearCost !== 0 && comparisonData.length > 0 ? {
                year: `${new Date(comparisonData[0].year).getFullYear() - yts + 1}-01-01`,
                age: comparisonData[0].age - yts + 1,
                magi_0: convYearMagi0, magi_n: convYearMagiN,
                irmaa_premium_partb_0: 0, irmaa_premium_partb_n: 0,
                irmaa_premium_partd_0: 0, irmaa_premium_partd_n: 0,
                irmaa_magi_min_0: null, irmaa_magi_max_0: null,
                irmaa_magi_min_n: null, irmaa_magi_max_n: null,
                irmaa_savings: irmaaConvYearCost,
                is_pre_dist: true,
              } : null;
              const allMedicareData = preDistRow ? [preDistRow, ...medicareData] : medicareData;
              const totPartB0 = medicareData.reduce((s, r) => s + (r.irmaa_premium_partb_0 || 0), 0);
              const totPartBN = medicareData.reduce((s, r) => s + (r.irmaa_premium_partb_n || 0), 0);
              const totPartD0 = medicareData.reduce((s, r) => s + (r.irmaa_premium_partd_0 || 0), 0);
              const totPartDN = medicareData.reduce((s, r) => s + (r.irmaa_premium_partd_n || 0), 0);
              const totPartBDelta = totPartB0 - totPartBN;
              const totPartDDelta = totPartD0 - totPartDN;
              const totSavings = totPartBDelta + totPartDDelta + (preDistRow ? irmaaConvYearCost : 0);
              const fmt = (v) => '$' + Math.round(v).toLocaleString('en-US');
              return (
            <TableContainer component={Paper}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5 }, '& tbody td:hover': CELL_HOVER_SPOTLIGHT }}>
                <TableHead sx={{ '& .MuiTableCell-root': { py: 0.25, borderBottom: '2px solid #000' } }}>
                  <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                    <TableCell rowSpan={2}>Year</TableCell>
                    <TableCell rowSpan={2}>Age</TableCell>
                    <TableCell align="right" rowSpan={2}>MAGI<br />Pre-Conv</TableCell>
                    <TableCell align="right" rowSpan={2}>MAGI<br />Post-Conv</TableCell>
                    <TableCell align="center" colSpan={2} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999', fontWeight: 'bold', py: 0.1 }}>2 Year MAGI Lookback</TableCell>
                    <TableCell align="center" colSpan={3} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999', fontWeight: 'bold', py: 0.1 }}>Part B</TableCell>
                    <TableCell align="center" colSpan={3} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999', fontWeight: 'bold', py: 0.1 }}>Part D</TableCell>
                    <TableCell align="right" rowSpan={2} sx={{ borderLeft: '2px solid #1976d2', backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>Total<br />Savings<br /><strong style={{ color: totSavings > 0 ? '#2e7d32' : 'inherit' }}>{fmt(totSavings)}</strong></TableCell>
                  </TableRow>
                  <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                    <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>IRMAA Bracket<br />Pre-Conversion</TableCell>
                    <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>IRMAA Bracket<br />Post-Conversion</TableCell>
                    <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>Pre-Conv<br />{fmt(totPartB0)}</TableCell>
                    <TableCell align="right">Post-Conv<br />{fmt(totPartBN)}</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Savings<br /><strong style={{ color: totPartBDelta > 0 ? '#2e7d32' : 'inherit' }}>{fmt(totPartBDelta)}</strong></TableCell>
                    <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>Pre-Conv<br />{fmt(totPartD0)}</TableCell>
                    <TableCell align="right">Post-Conv<br />{fmt(totPartDN)}</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Savings<br /><strong style={{ color: totPartDDelta > 0 ? '#2e7d32' : 'inherit' }}>{fmt(totPartDDelta)}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonLoading ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center">Loading...</TableCell>
                    </TableRow>
                  ) : comparisonData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center">No comparison data available</TableCell>
                    </TableRow>
                  ) : (
                    allMedicareData
                      .map((row, index) => {
                        const partbDelta = (row.irmaa_premium_partb_0 || 0) - (row.irmaa_premium_partb_n || 0);
                        const partdDelta = (row.irmaa_premium_partd_0 || 0) - (row.irmaa_premium_partd_n || 0);
                        const totalSavings = row.is_pre_dist ? (row.irmaa_savings || 0) : partbDelta + partdDelta;
                        // 2026-04-02: For IRMAA lookback year (conv_year + 2), show conversion-year MAGI
                        const convYear = new Date().getFullYear();
                        const rowYear = new Date(row.year).getFullYear();
                        const isConvLookbackYear = rowYear === convYear + 2;
                        const formatBracket = (min, max) => {
                          if (!min && min !== 0) return '—';
                          const fmtMin = '$' + Math.round(min).toLocaleString('en-US');
                          const fmtMax = max != null ? '$' + Math.round(max).toLocaleString('en-US') : '∞';
                          return `${fmtMin} – ${fmtMax}`;
                        };
                        // 2026-09-13: The pre-distribution row carries a summarized conversion-year
                        // surcharge, not an itemized Part B/D breakdown. Dash those cells — printing
                        // $0 would assert a zero surcharge rather than "not itemized". Total is exact.
                        const premium = (v) => row.is_pre_dist ? '—' : '$' + Math.round(v || 0).toLocaleString('en-US');
                        return (
                          <TableRow key={`medicare-${row.year}-${row.age}`} sx={{ backgroundColor: totalSavings > 0 ? '#f1f8e9' : totalSavings < 0 ? '#ffebee' : 'transparent' }}>
                            <TableCell>{new Date(row.year).getFullYear()}</TableCell>
                            <TableCell>{row.age}</TableCell>
                            <TableCell align="right">${Math.round(isConvLookbackYear ? convYearMagi0 : (row.magi_0 || 0)).toLocaleString('en-US')}</TableCell>
                            <TableCell align="right">${Math.round(isConvLookbackYear ? convYearMagiN : (row.magi_n || 0)).toLocaleString('en-US')}</TableCell>
                            <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>{formatBracket(row.irmaa_magi_min_0, row.irmaa_magi_max_0)}</TableCell>
                            <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>{formatBracket(row.irmaa_magi_min_n, row.irmaa_magi_max_n)}</TableCell>
                            <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>{premium(row.irmaa_premium_partb_0)}</TableCell>
                            <TableCell align="right">{premium(row.irmaa_premium_partb_n)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', color: row.is_pre_dist ? 'inherit' : partbDelta > 0 ? '#2e7d32' : partbDelta < 0 ? '#c62828' : 'inherit' }}>
                              {premium(partbDelta)}
                            </TableCell>
                            <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>{premium(row.irmaa_premium_partd_0)}</TableCell>
                            <TableCell align="right">{premium(row.irmaa_premium_partd_n)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', color: row.is_pre_dist ? 'inherit' : partdDelta > 0 ? '#2e7d32' : partdDelta < 0 ? '#c62828' : 'inherit' }}>
                              {premium(partdDelta)}
                            </TableCell>
                            <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', backgroundColor: '#e3f2fd', fontWeight: 'bold', color: totalSavings > 0 ? '#2e7d32' : totalSavings < 0 ? '#c62828' : 'inherit' }}>
                              ${Math.round(totalSavings).toLocaleString('en-US')}
                            </TableCell>
                          </TableRow>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
              );
            })()}
            {comparisonData.length > 0 && !(yts >= 2 && irmaaConvYearCost !== 0) && !comparisonData.some(row => (row.irmaa_premium_partb_0 || 0) > 0 || (row.irmaa_premium_partd_0 || 0) > 0) && (
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1, textAlign: 'center' }}>
                No IRMAA surcharges apply. MAGI stays below the lowest IRMAA threshold in all years.
              </Typography>
            )}
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', display: 'block', mt: 1 }}>
              Note: IRMAA surcharges use a 2-year lookback. Surcharge in year N is based on MAGI from year N-2.
            </Typography>
          </Box>
          )}

          {selectedYearlyView === 'irr_decomp' && (
          <Box sx={{ mb: 0 }}>
            {/* IRR Cash Flows and Conversion Alpha table */}
            {comparisonData.length > 0 && conversion && (() => {
              const r = inputInfo?.dist_return_assum || 0.07;
              const convTax = conversion.conv_tax || 0;
              const yearlyDrag = conversion.tax_drag_rmd_yearly || [];
              const yearlyHc = conversion.tax_drag_cont_yearly || [];
              const yearlySrc = conversion.src_dist_yearly || [];
              const firstYear = new Date(comparisonData[0].year).getFullYear();
              const firstAge = comparisonData[0].age;
              const yts = distributionSchedule?.years_to_start_year || 0;
              const convYear = firstYear - yts - 1; // actual conversion year (run_year)
              const isRMD = inputInfo?.distribution_method === 'R';

              // Build rows: outflow + gap years (zero) + Phase 1 (comparisonData) + beneficiary loop (FA only)
              const rows = [];
              let cumulPv = 0;
              let yearIndex = 0;

              // Year 0: conversion outflow (outside payment only)
              const outflow = inputInfo?.tax_payment_source === 'I' ? 0 : -convTax;
              // The PV side is the alpha decomposition, which has no SRC principal term:
              // the conversion-tax outlay is an SRC cash flow, not a component of value.
              const pv0 = 0;
              cumulPv += pv0;
              // The outlay is an SRC cash flow: Core is the conversion funded from inside, which
              // carries no outlay at all.
              rows.push({ year: convYear, age: firstAge - yts - 1, coreDelta: 0, srcDelta: outflow, irmaaCf: 0, drag: 0, hcDrag: 0, coreDeltaPv: 0, srcDeltaPv: outflow, irmaaPv: 0, dragPv: 0, hcDragPv: 0, totalCf: outflow, pv: pv0, cumulPv, isOutflow: true });
              yearIndex = 1;

              // Gap years (between conversion and first distribution)
              // g=1 (run_year+2): pre-distribution IRMAA surcharge from 2-yr lookback on conversion MAGI
              for (let g = 0; g < yts; g++) {
                const irmaaCf = g === 1 ? (conversion?.irmaa_surcharge_cost || 0) : 0;
                const disc = Math.pow(1 + r, yearIndex);
                const irmaaPv = irmaaCf !== 0 ? irmaaCf / disc : 0;
                cumulPv += irmaaPv;
                rows.push({
                  year: convYear + g + 1,
                  age: firstAge - yts + g,
                  coreDelta: 0, srcDelta: 0, irmaaCf, drag: 0, hcDrag: 0,
                  coreDeltaPv: 0, srcDeltaPv: 0, irmaaPv, dragPv: 0, hcDragPv: 0,
                  totalCf: irmaaCf, pv: irmaaPv, cumulPv,
                  isGap: true,
                });
                yearIndex++;
              }

              // Phase 1 (RMD: dist + ben merged in comparisonData; FA: dist only)
              const benThreshold = isRMD ? comparisonData.length - 10 : comparisonData.length;
              comparisonData.forEach((row, i) => {
                const distDelta = row.atcf_change || 0;  // 2026-10-05: atcf_change is distributions only now
                const irmaaCf = row.irmaa_savings || 0;
                // yearly_rmd / yearly_hc / src_dist are indexed from year 1 after conversion:
                // index 0 = first gap year. For first dist year (i=0), need index = yts.
                const dragIdx = i + yts;
                const drag = dragIdx < yearlyDrag.length ? yearlyDrag[dragIdx] : 0;
                const hcDrag = dragIdx < yearlyHc.length ? yearlyHc[dragIdx] : 0;
                // SRC = the conversion-tax dollars left in the Roth, distributing at the sheltered
                // rate. What remains of the ATCF delta is the rate arbitrage; it sums to spread_fv_tw.
                const srcDelta = dragIdx < yearlySrc.length ? yearlySrc[dragIdx] : 0;
                const coreDelta = distDelta - srcDelta;
                // 8d17a8a: the SRC drag is NOT in the IRR stream — the SRC column already carries
                // those dollars at the sheltered rate, so adding the gap counts the shelter twice.
                const totalCf = coreDelta + srcDelta + irmaaCf + drag;
                const disc = Math.pow(1 + r, yearIndex);
                const coreDeltaPv = coreDelta / disc;
                const srcDeltaPv = srcDelta / disc;
                const irmaaPv = irmaaCf / disc;
                const dragPv = drag / disc;
                const hcDragPv = hcDrag / disc;
                const pv = coreDeltaPv + irmaaPv + dragPv + hcDragPv;
                cumulPv += pv;
                rows.push({
                  year: new Date(row.year).getFullYear(),
                  age: row.age,
                  coreDelta, srcDelta, irmaaCf, drag, hcDrag,
                  coreDeltaPv, srcDeltaPv, irmaaPv, dragPv, hcDragPv,
                  totalCf, pv, cumulPv,
                  isBeneficiary: i >= benThreshold,
                });
                yearIndex++;
              });

              // Beneficiary loop: FA only — comparisonData stops at life_years end, drag values for ben come from yearlyDrag tail
              if (!isRMD) {
                const phase1Years = comparisonData.length;
                for (let i = 0; i < 10; i++) {
                  const dragIdx = phase1Years + i + yts;
                  const drag = dragIdx < yearlyDrag.length ? yearlyDrag[dragIdx] : 0;
                  const hcDrag = dragIdx < yearlyHc.length ? yearlyHc[dragIdx] : 0;
                  const srcDelta = dragIdx < yearlySrc.length ? yearlySrc[dragIdx] : 0;
                  if (drag === 0 && hcDrag === 0 && srcDelta === 0 && i > 0) break;
                  const totalCf = srcDelta + drag;
                  const disc = Math.pow(1 + r, yearIndex);
                  const srcDeltaPv = srcDelta / disc;
                  const dragPv = drag / disc;
                  const hcDragPv = hcDrag / disc;
                  const pv = dragPv + hcDragPv;
                  cumulPv += pv;
                  rows.push({
                    year: firstYear + phase1Years + i,
                    age: firstAge + phase1Years + i,
                    coreDelta: 0, srcDelta, irmaaCf: 0, drag, hcDrag,
                    coreDeltaPv: 0, srcDeltaPv, irmaaPv: 0, dragPv, hcDragPv,
                    totalCf, pv, cumulPv,
                    isBeneficiary: true,
                  });
                  yearIndex++;
                }
              }

              // Compute column sums
              const sums = rows.reduce((acc, row) => ({
                coreDelta: acc.coreDelta + row.coreDelta,
                srcDelta: acc.srcDelta + row.srcDelta,
                irmaaCf: acc.irmaaCf + row.irmaaCf,
                drag: acc.drag + row.drag,
                hcDrag: acc.hcDrag + row.hcDrag,
                coreDeltaPv: acc.coreDeltaPv + row.coreDeltaPv,
                srcDeltaPv: acc.srcDeltaPv + row.srcDeltaPv,
                irmaaPv: acc.irmaaPv + row.irmaaPv,
                dragPv: acc.dragPv + row.dragPv,
                hcDragPv: acc.hcDragPv + row.hcDragPv,
                totalCf: acc.totalCf + row.totalCf,
                pv: acc.pv + row.pv,
              }), { coreDelta: 0, srcDelta: 0, irmaaCf: 0, drag: 0, hcDrag: 0, coreDeltaPv: 0, srcDeltaPv: 0, irmaaPv: 0, dragPv: 0, hcDragPv: 0, totalCf: 0, pv: 0 });

              // Anything that rounds to zero prints a plain $0 in black: -0.01 should not
              // render as a red "-$0" just because the sign survived the rounding.
              const fmt = (v) => { const n = Math.round(v); return `$${(n === 0 ? 0 : n).toLocaleString('en-US')}`; };
              const fmtColor = (v) => (Math.round(v) === 0 ? {} : { color: v >= 0 ? 'success.main' : 'error.main' });

              return (
              <Box sx={{ mt: 0 }}>
                <Typography variant="h6" sx={{ textAlign: 'center', mb: 0 }}>
                  IRR Cash Flows and Conversion Alpha — Group {conversion.conv_group_num} ({(conversion.tax_rate_bucket * 100).toFixed(1)}% bracket)
                </Typography>
                <Typography variant="caption" color="text.primary" sx={{ display: 'block', textAlign: 'center', mb: 0 }}>
                  IRR = {(conversion.conv_irr * 100).toFixed(2)}% | Total Conversion Alpha @ {(r * 100).toFixed(0)}% = {fmt(cumulPv)}
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.25 }, '& tbody td:hover': CELL_HOVER_SPOTLIGHT }}>
                    <TableHead sx={{ '& .MuiTableCell-root': { py: 0, lineHeight: 1.1, borderBottom: '2px solid #000' } }}>
                      <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                        <TableCell rowSpan={2}>Year</TableCell>
                        <TableCell rowSpan={2}>Age</TableCell>
                        <TableCell align="center" colSpan={4} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999' }}>IRR Nominal Cash Flows</TableCell>
                        <TableCell align="right" rowSpan={2} sx={{ borderLeft: '2px solid #1976d2', fontWeight: 'bold' }}>IRR<br/>Cash<br/>Flows</TableCell>
                        <TableCell align="center" colSpan={4} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999' }}>Net Present Values @ {(r * 100).toFixed(0)}%</TableCell>
                        <TableCell align="right" rowSpan={2} sx={{ borderLeft: '2px solid #1976d2', fontWeight: 'bold' }}>Total<br/>Conversion<br/>Alpha</TableCell>
                        <TableCell align="right" rowSpan={2} sx={{ borderLeft: '2px solid #1976d2' }}>Tax-Drag<br/>SRC<br/>Nominal</TableCell>
                        <TableCell align="right" rowSpan={2}>Synthetic Roth<br/>Contribution<br/>SRC ΔATCF PV</TableCell>
                      </TableRow>
                      <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                        <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>Rate-Arb<br/>ΔATCF</TableCell>
                        <TableCell align="right">IRMAA</TableCell>
                        <TableCell align="right">Tax-Drag<br/>RMD</TableCell>
                        <TableCell align="right">SRC<br/>ΔATCF</TableCell>
                        <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>Rate-Arb<br/>ΔATCF</TableCell>
                        <TableCell align="right">IRMAA</TableCell>
                        <TableCell align="right">Tax-Drag<br/>RMD</TableCell>
                        <TableCell align="right">Tax-Drag<br/>SRC</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, i) => (
                        <TableRow key={i} sx={{
                          ...(row.isOutflow ? { backgroundColor: '#fff3e0' } : {}),
                          ...(row.isBeneficiary ? { backgroundColor: '#f3e5f5' } : {}),
                          ...(row.isGap ? { color: 'text.secondary', fontStyle: 'italic' } : {}),
                        }}>
                          <TableCell>{row.year}</TableCell>
                          <TableCell>{row.age}{row.isBeneficiary ? '*' : ''}</TableCell>
                          <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', ...(row.coreDelta !== 0 ? fmtColor(row.coreDelta) : {}) }}>{row.coreDelta !== 0 ? fmt(row.coreDelta) : ''}</TableCell>
                          <TableCell align="right" sx={row.irmaaCf !== 0 ? fmtColor(row.irmaaCf) : {}}>{row.irmaaCf !== 0 ? fmt(row.irmaaCf) : ''}</TableCell>
                          <TableCell align="right" sx={row.drag !== 0 ? fmtColor(row.drag) : {}}>{row.drag !== 0 ? fmt(row.drag) : ''}</TableCell>
                          <TableCell align="right" sx={row.srcDelta !== 0 ? fmtColor(row.srcDelta) : {}}>{row.srcDelta !== 0 ? fmt(row.srcDelta) : ''}</TableCell>
                          <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', fontWeight: 'bold', ...fmtColor(row.totalCf) }}>{fmt(row.totalCf)}</TableCell>
                          <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', ...(row.coreDeltaPv !== 0 ? fmtColor(row.coreDeltaPv) : {}) }}>{row.coreDeltaPv !== 0 ? fmt(row.coreDeltaPv) : ''}</TableCell>
                          <TableCell align="right" sx={row.irmaaPv !== 0 ? fmtColor(row.irmaaPv) : {}}>{row.irmaaPv !== 0 ? fmt(row.irmaaPv) : ''}</TableCell>
                          <TableCell align="right" sx={row.dragPv !== 0 ? fmtColor(row.dragPv) : {}}>{row.dragPv !== 0 ? fmt(row.dragPv) : ''}</TableCell>
                          <TableCell align="right" sx={row.hcDragPv !== 0 ? fmtColor(row.hcDragPv) : {}}>{row.hcDragPv !== 0 ? fmt(row.hcDragPv) : ''}</TableCell>
                          <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', fontWeight: 'bold', ...fmtColor(row.pv) }}>{fmt(row.pv)}</TableCell>
                          <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>{row.hcDrag !== 0 ? fmt(row.hcDrag) : ''}</TableCell>
                          <TableCell align="right" sx={row.srcDeltaPv !== 0 ? fmtColor(row.srcDeltaPv) : {}}>{row.srcDeltaPv !== 0 ? fmt(row.srcDeltaPv) : ''}</TableCell>
                        </TableRow>
                      ))}
                      {/* Sums row */}
                      <TableRow sx={{ '& .MuiTableCell-root': { borderTop: '2px solid #000', fontWeight: 'bold', py: 0.5 } }}>
                        <TableCell colSpan={2}>Total</TableCell>
                        <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', ...fmtColor(sums.coreDelta) }}>{fmt(sums.coreDelta)}</TableCell>
                        <TableCell align="right" sx={fmtColor(sums.irmaaCf)}>{fmt(sums.irmaaCf)}</TableCell>
                        <TableCell align="right" sx={fmtColor(sums.drag)}>{fmt(sums.drag)}</TableCell>
                        <TableCell align="right" sx={fmtColor(sums.srcDelta)}>{fmt(sums.srcDelta)}</TableCell>
                        <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', ...fmtColor(sums.totalCf) }}>{fmt(sums.totalCf)}</TableCell>
                        <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', ...fmtColor(sums.coreDeltaPv) }}>{fmt(sums.coreDeltaPv)}</TableCell>
                        <TableCell align="right" sx={fmtColor(sums.irmaaPv)}>{fmt(sums.irmaaPv)}</TableCell>
                        <TableCell align="right" sx={fmtColor(sums.dragPv)}>{fmt(sums.dragPv)}</TableCell>
                        <TableCell align="right" sx={fmtColor(sums.hcDragPv)}>{fmt(sums.hcDragPv)}</TableCell>
                        <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2', ...fmtColor(sums.pv) }}>{fmt(sums.pv)}</TableCell>
                        <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>{fmt(sums.hcDrag)}</TableCell>
                        <TableCell align="right" sx={fmtColor(sums.srcDeltaPv)}>{fmt(sums.srcDeltaPv)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  * Beneficiary period (10-year inherited distribution). Orange row = conversion tax outflow. Purple rows = beneficiary period.
                </Typography>
              </Box>
              );
            })()}
          </Box>
          )}

          {selectedYearlyView === 'ss_tax_reduction' && (
          <Box sx={{ mb: 0.25, mt: 0.5, pt: 0, px: 1.5, pb: 0, backgroundColor: '#ffffff', borderRadius: 1, border: '1.5px solid #2e7d32' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0, color: '#2e7d32' }}>
              Social Security Tax Reduction
            </Typography>
            {!hasDistributionTorpedo && (
              <Box sx={{ mb: 1, p: 1.5, backgroundColor: '#fffde7', borderLeft: '3px solid #f9a825', borderRadius: 1 }}>
                <Typography variant="body2">
                  This conversion does <strong>not</strong> reduce taxable Social Security in any future distribution year — no SS torpedo zone is being escaped. Therefore no extra Conversion alpha from Social Security tax reduction. Year-by-year detail is shown below for reference.
                </Typography>
              </Box>
            )}
            {(() => {
              const ss85Offset = inputInfo?.distribution_status === 'M' ? 36942.18 : 28706.88;
              const fmt = (v) => '$' + Math.round(v).toLocaleString('en-US');
              const fmtPct = (v) => (v * 100).toFixed(0) + '%';
              return (
              <TableContainer component={Paper}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.25 }, '& tbody td:hover': CELL_HOVER_SPOTLIGHT }}>
                  <TableHead sx={{ '& .MuiTableCell-root': { py: 0, lineHeight: 1.1, borderBottom: '2px solid #000' } }}>
                    <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                      <TableCell align="center" rowSpan={2}>Year</TableCell>
                      <TableCell align="center" rowSpan={2}>Age</TableCell>
                      <TableCell align="center" rowSpan={2}>SS<br />Benefit</TableCell>
                      <TableCell align="center" colSpan={3} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999', fontWeight: 'bold', py: 0.1 }}>Provisional Income</TableCell>
                      <TableCell align="center" colSpan={3} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999', fontWeight: 'bold', py: 0.1 }}>Taxable Social Security</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999', fontWeight: 'bold', py: 0.1 }}>% Taxed</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999', fontWeight: 'bold', py: 0.1 }}>MTR Post-Conv</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ borderLeft: '2px solid #1976d2', borderBottom: '1px solid #999', fontWeight: 'bold', py: 0.1 }}>MTR Pre-Conv</TableCell>
                    </TableRow>
                    <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                      <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>Pre<br />Conversion</TableCell>
                      <TableCell align="center">Post<br />Conversion</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: '1%' }}>To Max Tax<br />SS Benefit</TableCell>
                      <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>Pre<br />Conversion</TableCell>
                      <TableCell align="center">Post<br />Conversion</TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>Reduction</TableCell>
                      <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>Pre<br />Conversion</TableCell>
                      <TableCell align="center">Post<br />Conversion</TableCell>
                      <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>Not SS<br />Adjusted</TableCell>
                      <TableCell align="center">Actual<br />Adjusted</TableCell>
                      <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>Not SS<br />Adjusted</TableCell>
                      <TableCell align="center">Actual<br />Adjusted</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonLoading ? (
                      <TableRow>
                        <TableCell colSpan={15} align="center">Loading...</TableCell>
                      </TableRow>
                    ) : comparisonData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={15} align="center">No comparison data available</TableCell>
                      </TableRow>
                    ) : (
                      (inputInfo?.distribution_method === 'R' && inputInfo?.life_years
                        ? comparisonData.slice(0, inputInfo.life_years)
                        : comparisonData)
                        .map((row, index) => {
                          const taxSSSavings = (row.taxable_ss_opt_0 || 0) - (row.taxable_ss_opt_n || 0);
                          return (
                            <TableRow key={`ss-${row.year}-${row.age}`} sx={{ backgroundColor: taxSSSavings > 0 ? '#f1f8e9' : 'transparent' }}>
                              <TableCell>{new Date(row.year).getFullYear()}</TableCell>
                              <TableCell>{row.age}</TableCell>
                              <TableCell align="right">{fmt(row.soc_sec_n || 0)}</TableCell>
                              <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>{fmt(row.prov_income_0 || 0)}</TableCell>
                              <TableCell align="right">{fmt(row.prov_income_n || 0)}</TableCell>
                              <TableCell align="right">{fmt((row.soc_sec_n || 0) + ss85Offset)}</TableCell>
                              <TableCell align="right" sx={{ borderLeft: '2px solid #1976d2' }}>{fmt(row.taxable_ss_opt_0 || 0)}</TableCell>
                              <TableCell align="right">{fmt(row.taxable_ss_opt_n || 0)}</TableCell>
                              <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', fontWeight: taxSSSavings > 0 ? 'bold' : 'normal', color: taxSSSavings > 0 ? '#2e7d32' : 'inherit' }}>
                                {fmt(taxSSSavings)}
                              </TableCell>
                              <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2' }}>{fmtPct(row.pct_ss_taxed_opt_0 || 0)}</TableCell>
                              <TableCell align="center">{fmtPct(row.pct_ss_taxed_opt_n || 0)}</TableCell>
                              {(() => {
                                const mtrN = ((row.trad_mtr_n || 0) * 100).toFixed(1);
                                const adjN = ((row.trad_mtr_adj_n || 0) * 100).toFixed(1);
                                const differN = mtrN !== adjN;
                                return (<>
                                  <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2', fontWeight: differN ? 'bold' : 'normal' }}>{mtrN}%</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: differN ? 'bold' : 'normal' }}>{adjN}%</TableCell>
                                </>);
                              })()}
                              {(() => {
                                const mtr0 = ((row.trad_mtr_0 || 0) * 100).toFixed(1);
                                const adj0 = ((row.trad_mtr_adj_0 || 0) * 100).toFixed(1);
                                const differ = mtr0 !== adj0;
                                return (<>
                                  <TableCell align="center" sx={{ borderLeft: '2px solid #1976d2', fontWeight: differ ? 'bold' : 'normal' }}>{mtr0}%</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: differ ? 'bold' : 'normal' }}>{adj0}%</TableCell>
                                </>);
                              })()}
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              );
            })()}
            {hasDistributionTorpedo && (
              <Box sx={{ mt: 0.25, p: 1.5, backgroundColor: '#e8f5e9', borderLeft: '3px solid #2e7d32', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This conversion pushes future distributions below the SS-85%-cap threshold and into the <strong>Torpedo Zone</strong> — where each distribution dollar drags <strong>$0.85</strong> of taxable Social Security with it, jacking the effective marginal rate up to <strong>1.85× the posted bracket</strong>. The <strong>Actual Adjusted</strong> columns show what's really being paid per dollar; <strong>Not SS Adjusted</strong> shows just the posted bracket. The gap between them IS the Torpedo Zone, in action.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Converting now strips distribution dollars out of this Torpedo Zone — tax savings are hugely amplified at 1.5× to 1.85× the posted bracket rate, producing outsized profits across the green-shaded retirement years shown above.
                </Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', display: 'block', mt: 1 }}>
              Note: <strong>Provisional Income = SS Benefit ÷ 2 + Traditional Distributions + Other Taxable Income</strong>. SS taxation thresholds are not inflation-adjusted.
            </Typography>
          </Box>
          )}
          </Box>
        </Collapse>

        <TableContainer sx={{ boxSizing: 'border-box' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { px: 1.5, py: 1, borderLeft: 'none', borderRight: 'none' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderTop: 'none', borderBottom: 'none' }} />
                <TableCell align="right" sx={{ fontWeight: 'bold', width: '12%', borderTop: 'none', borderBottom: 'none' }}>Present Value</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', width: '12%', borderTop: 'none', borderBottom: 'none' }}>Future Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* 2026-04-20: Reordered — WEALTH EFFECTS at top, Hidden Contribution (renumbered #4) moved below subtotal, above total row */}
              {/* Wealth Effects label */}
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0.5, pb: 0 } }}>
                <TableCell colSpan={3} sx={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#777', fontStyle: 'italic' }}>WEALTH EFFECTS FROM CONVERTING ${conversion.conv_amt?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} — the Alpha!</Typography>
                </TableCell>
              </TableRow>
              {/* Tax-Rate Spread row */}
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0.5, pb: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>1) Tax-Rate Spread Arbitrage</Typography>
                    <Tooltip arrow placement="right" {...BLUE_TOOLTIP_PROPS} title={
                      <Box>
                        <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>Tax-Rate Spread Arbitrage</Typography>
                        <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.6 }}>
                          Conversion locks in today{"'"}s tax rate (t<sub>C</sub>) on the converted amount. If your future distribution tax rate (t<sub>D</sub>) would have been higher, you save the spread; if lower, you lose it. Computed on a present-value, time-weighted basis using actual year-by-year distribution tax rates.
                        </Typography>
                      </Box>
                    }>
                      <IconButton size="small" sx={{ p: 0, color: '#1976d2' }}>
                        <InfoIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: Math.round(conversion.spread_pv_tw || 0) > 0 ? '#e8f5e9' : Math.round(conversion.spread_pv_tw || 0) < 0 ? '#ffebee' : 'transparent' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: Math.round(conversion.spread_pv_tw || 0) > 0 ? 'success.main' : Math.round(conversion.spread_pv_tw || 0) < 0 ? 'error.main' : 'text.primary' }}>
                    ${Math.round(conversion.spread_pv_tw || 0)?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: Math.round(convSpreadFv) > 0 ? '#e8f5e9' : Math.round(convSpreadFv) < 0 ? '#ffebee' : 'transparent' }}>
                  <Typography variant="body1" sx={{ color: Math.round(convSpreadFv) > 0 ? 'success.main' : Math.round(convSpreadFv) < 0 ? 'error.main' : 'text.primary' }}>
                    ${Math.round(convSpreadFv)?.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* 2) Medicare Surcharge Impact — header row, no numbers; sub-rows 2a/2b show the two effects */}
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0.5, pb: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>2) Medicare Surcharge Impact</Typography>
                    <Tooltip arrow placement="right" {...BLUE_TOOLTIP_PROPS} title={
                      <Box>
                        <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>Medicare Surcharge Impact</Typography>
                        <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.6 }}>
                          Roth conversions affect Medicare Part B and Part D premiums via IRMAA (Income-Related Monthly Adjustment Amount). Medicare uses a 2-year MAGI lookback, which creates two opposing effects: <strong>(a)</strong> lower MAGI in retirement years (smaller RMDs) saves on premiums; <strong>(b)</strong> the conversion year's higher MAGI triggers a one-time premium increase 2 years later.
                        </Typography>
                      </Box>
                    }>
                      <IconButton size="small" sx={{ p: 0, color: '#1976d2' }}>
                        <InfoIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
              {/* 2a) Future Savings */}
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0, pb: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      <Box component="span" sx={{ display: 'inline-block', width: '24px' }} />
                      a) Future Surcharge Savings
                    </Typography>
                    <Tooltip arrow placement="right" {...BLUE_TOOLTIP_PROPS} title={
                      <Box>
                        <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>Medicare Future Surcharge Savings</Typography>
                        <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.6 }}>
                          {inputInfo?.medicare_coverage === 'A'
                            ? 'You only have Medicare Part A, which has no income-based surcharge — so lower future MAGI does not affect your Medicare costs.'
                            : `Conversion shrinks future RMDs, lowering MAGI in retirement years. Medicare looks back at your MAGI from 2 years prior to set IRMAA surcharges on Part B and Part D — so a lower MAGI in those years saves on premiums${userInfo?.marital_status === 'M' && userInfo?.birth_date_spouse ? ' for both you and your spouse (both age 65+)' : ''}. Aggregated across all future years on a present-value basis.`}
                        </Typography>
                      </Box>
                    }>
                      <IconButton size="small" sx={{ p: 0, color: '#1976d2' }}>
                        <InfoIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: Math.round(irmaaFutureSavingsPv) > 0 ? '#e8f5e9' : 'transparent' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: Math.round(irmaaFutureSavingsPv) > 0 ? 'success.main' : 'text.primary' }}>
                    ${Math.round(irmaaFutureSavingsPv)?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: Math.round(irmaaFutureSavings) > 0 ? '#e8f5e9' : 'transparent' }}>
                  <Typography variant="body1" sx={{ color: Math.round(irmaaFutureSavings) > 0 ? 'success.main' : 'text.primary' }}>
                    ${Math.round(irmaaFutureSavings)?.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
              {/* 2b) Conversion-Year Cost */}
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0, pb: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      <Box component="span" sx={{ display: 'inline-block', width: '24px' }} />
                      b) 2-Year Forward Surcharge Cost
                    </Typography>
                    <Tooltip arrow placement="right" {...BLUE_TOOLTIP_PROPS} title={
                      <Box>
                        <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>Medicare 2-Year Forward Surcharge Cost</Typography>
                        <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.6 }}>
                          {inputInfo?.medicare_coverage === 'A'
                            ? 'You only have Medicare Part A, which has no income-based surcharge — so the conversion-year MAGI bump does not affect your Medicare costs.'
                            : `Conversion bumps your MAGI in the conversion year. Medicare uses MAGI from 2 years prior to set IRMAA, so this triggers a one-time premium increase 2 years after the conversion${userInfo?.marital_status === 'M' && userInfo?.birth_date_spouse ? ' (applied to both eligible spouses)' : ''}. Often offset by ongoing Future Savings (#2a).`}
                        </Typography>
                      </Box>
                    }>
                      <IconButton size="small" sx={{ p: 0, color: '#1976d2' }}>
                        <InfoIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: Math.round(irmaaConvYearCostPv) < 0 ? '#ffebee' : 'transparent' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: Math.round(irmaaConvYearCostPv) < 0 ? 'error.main' : Math.round(irmaaConvYearCostPv) > 0 ? 'success.main' : 'text.primary' }}>
                    ${Math.round(irmaaConvYearCostPv)?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: Math.round(irmaaConvYearCost) < 0 ? '#ffebee' : 'transparent' }}>
                  <Typography variant="body1" sx={{ color: Math.round(irmaaConvYearCost) < 0 ? 'error.main' : Math.round(irmaaConvYearCost) > 0 ? 'success.main' : 'text.primary' }}>
                    ${Math.round(irmaaConvYearCost)?.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* 3) Tax-Drag Shelter — header row, no numbers; sub-rows 3a/3b show the two mechanisms */}
              {/* 2026-05-09: Both HC drag and RMD drag are additive alpha — same coin, different angles. Quantifies the retirement-account raison d'être. */}
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0.5, pb: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>3) Tax-Drag Shelter</Typography>
                    <Tooltip arrow placement="right" {...BLUE_TOOLTIP_PROPS} title={
                      <Box>
                        <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>Tax-Drag Shelter — the retirement account's raison d'être</Typography>
                        <Typography variant="body2" sx={{ display: 'block', mb: 0.75, lineHeight: 1.6 }}>
                          Every dollar inside a tax-deferred or tax-free account compounds drag-free. The same dollar in a taxable account loses yield every year to tax drag (taxes on dividends, interest, and realized gains). Over decades, drag avoidance is the dominant source of retirement-account value — yet most calculators bury it inside an opaque "expected return" assumption and never quantify it.
                        </Typography>
                        <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.6 }}>
                          A Roth conversion creates Tax-Drag Shelter alpha two ways: <strong>(a)</strong> by shrinking future Trad RMDs so dollars stay sheltered instead of being forced out and reinvested taxably, and <strong>(b)</strong> by moving the conversion-tax dollar from a taxable bank account into Roth. Same coin, different angles.
                        </Typography>
                      </Box>
                    }>
                      <IconButton size="small" sx={{ p: 0, color: '#1976d2' }}>
                        <InfoIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#888' }}>The reason retirement accounts exist — <Box component="span" sx={{ fontWeight: 'bold' }}>Quantified</Box></Typography>
                  </Box>
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
              {/* 3a) Reduced RMDs */}
              <TableRow
                onClick={() => onNavigateToChart?.('cp-chart-rmd-drag')}
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e3f2fd' }, '& .MuiTableCell-root': { pt: 0, pb: 0 } }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    <Box component="span" sx={{ display: 'inline-block', width: '24px' }} />
                    a) Benefit of Reduced RMDs
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: Math.round(conversion.tax_drag_rmd_pv || 0) > 0 ? '#e8f5e9' : 'transparent' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: Math.round(conversion.tax_drag_rmd_pv || 0) > 0 ? 'success.main' : 'text.primary' }}>
                    ${Math.round(conversion.tax_drag_rmd_pv || 0)?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: Math.round(conversion.tax_drag_rmd_fv || 0) > 0 ? '#e8f5e9' : 'transparent' }}>
                  <Typography variant="body1" sx={{ color: Math.round(conversion.tax_drag_rmd_fv || 0) > 0 ? 'success.main' : 'text.primary' }}>
                    ${Math.round(conversion.tax_drag_rmd_fv || 0)?.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
              {/* 3b) Hidden Contribution drag — only when outside payment */}
              {inputInfo?.tax_payment_source === 'O' && (
                <TableRow
                  onClick={() => onNavigateToChart?.('cp-chart-hc-drag')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e3f2fd' }, '& .MuiTableCell-root': { pt: 0, pb: 0 } }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      <Box component="span" sx={{ display: 'inline-block', width: '24px' }} />
                      b) Benefit of moving taxable ${Math.round(conversion.conv_tax || 0)?.toLocaleString()} into tax-free Roth
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ backgroundColor: Math.round(srcShelterEarnedPv(conversion)) > 0 ? '#e8f5e9' : 'transparent' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: Math.round(srcShelterEarnedPv(conversion)) > 0 ? 'success.main' : 'text.primary' }}>
                      ${Math.round(srcShelterEarnedPv(conversion))?.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ backgroundColor: Math.round(srcShelterEarnedFv(conversion)) > 0 ? '#e8f5e9' : 'transparent' }}>
                    <Typography variant="body1" sx={{ color: Math.round(srcShelterEarnedFv(conversion)) > 0 ? 'success.main' : 'text.primary' }}>
                      ${Math.round(srcShelterEarnedFv(conversion))?.toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* Immediate Wealth Change subtotal (items 1+2+3a+3b) */}
              {/* 2026-09-27: engine-computed totals. They gate the SRC shelter on the funding
                  source, which a sum here cannot do once tax_drag_cont_pv carries its real
                  value on Inside runs. */}
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0.5, pb: 0 } }}>
                <TableCell sx={{ borderBottom: 'none', textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold', color: '#333' }}>Change in Wealth due to Conversion →</Typography>
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: 'none' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: convAlphaPvTotal >= 0 ? 'success.main' : 'error.main' }}>
                    ${Math.round(convAlphaPvTotal)?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: 'none' }}>
                  <Typography variant="body1" sx={{ color: convAlphaFvTotal >= 0 ? 'success.main' : 'error.main' }}>
                    ${Math.round(convAlphaFvTotal)?.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* Conditional note when PV and FV tell different stories */}
              {(convAlphaPvTotal >= 0) !== (convAlphaFvTotal >= 0) && (
                <TableRow sx={{ '& .MuiTableCell-root': { pt: 0, pb: 0 } }}>
                  <TableCell colSpan={3} sx={{ borderTop: 'none', borderBottom: 'none', textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Nearer term Medicare savings have greater present value than larger late term distribution reductions due to tax-rate spread loss. Conversion decision is based on present value.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* 2026-04-20: Hidden Contribution (renumbered #4) — moved from top to sit above Total and next to Inside/Outside toggle */}
              {/* Tax Payment Source Effect label */}
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0.5, pb: 0 } }}>
                <TableCell colSpan={3} sx={{ borderTop: 'none', borderBottom: '1px solid #000', textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#777', fontStyle: 'italic' }}>ASSET TRANSFER EFFECT — {inputInfo?.tax_payment_source === 'I' ? 'None (tax paid inside)' : `Moving $${Math.round(conversion.conv_tax || 0).toLocaleString('en-US')} from Bank to Roth`}</Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '& .MuiTableCell-root': { pt: 0.5, pb: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{inputInfo?.tax_payment_source === 'I' ? '4) No Hidden Contribution' : "4) 'Hidden' Roth Contribution"}</Typography>
                    {inputInfo?.tax_payment_source !== 'I' && distributionSchedule?.annuity_factor_multiple && (
                      <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        (× {distributionSchedule.annuity_factor_multiple.toFixed(2)} annuity factor)
                      </Typography>
                    )}
                    <Tooltip arrow placement="right" {...BLUE_TOOLTIP_PROPS} title={
                      <Box>
                        <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>{inputInfo?.tax_payment_source === 'I' ? 'No Hidden Contribution' : "'Hidden' Roth Contribution"}</Typography>
                        <Typography variant="body2" sx={{ display: 'block', mb: 0.75, lineHeight: 1.6 }}>
                          {inputInfo?.tax_payment_source === 'I'
                            ? 'Conversion tax is paid from inside the retirement account, so no outside cash is spent. The Roth receives (conv_amt − conv_tax) and there is no hidden contribution — just a partial transfer between buckets.'
                            : "When the conversion tax is paid from outside (e.g., a bank account), the full converted amount enters Roth. The cash you spent on tax is effectively a Roth contribution — moved from a taxable bank account into a tax-free Roth — that grows tax-free for the rest of your life. This is the conversion's most overlooked source of value."}
                        </Typography>
                        {inputInfo?.tax_payment_source !== 'I' && (
                          <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.6 }}>
                            <strong>Decomposition shown below:</strong> the gross Hidden Contribution value is composed of <strong>(a) the Tax-Drag Alpha Component</strong> — the Roth-over-bank growth advantage, also credited as alpha in section 3b — and <strong>(b) the Counterfactual Component</strong> — what the conv_tax dollar would have been worth growing taxably at r·(1−drag) ({((inputInfo?.dist_return_assum || 0) * 100).toFixed(0)}·(1−{(inputInfo?.tax_drag_assum || 0).toString().replace(/^0/, '')}) = {((inputInfo?.dist_return_assum || 0) * (1 - (inputInfo?.tax_drag_assum || 0)) * 100).toFixed(2)}%) in a taxable bank. Only (b) flows into the grand total — adding (a) would double-count the drag alpha already in section 3b. This decomposition makes a silent assumption in standard conversion analysis explicit: the conventional 'Hidden Contribution' figure implicitly assumes the bank counterfactual = zero, when in reality those dollars would have grown, just at a drag-reduced rate.
                          </Typography>
                        )}
                      </Box>
                    }>
                      <IconButton size="small" sx={{ p: 0, color: '#1976d2' }}>
                        <InfoIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body1">
                    ${Math.round(inputInfo?.tax_payment_source === 'I' ? 0 : (conversion.conv_tax || 0)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body1">
                    ${Math.round(convHcFv)?.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
              {/* 4a) Tax-Drag Alpha Component — informational, already counted in section 3b, NOT additive */}
              {inputInfo?.tax_payment_source !== 'I' && (conversion.tax_drag_cont_pv || 0) > 0 && (
                <TableRow sx={{ '& .MuiTableCell-root': { pt: 0, pb: 0, borderBottom: 'none' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      <Box component="span" sx={{ display: 'inline-block', width: '24px' }} />
                      a) Tax-Drag Alpha Component from 3b
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      ${Math.round(conversion.tax_drag_cont_pv || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      ${Math.round(conversion.tax_drag_cont_fv || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {/* 4b) Counterfactual Component — bolded, additive to grand total */}
              {inputInfo?.tax_payment_source !== 'I' && (
                <TableRow sx={{ '& .MuiTableCell-root': { pt: 0, pb: 0, borderBottom: 'none' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      <Box component="span" sx={{ display: 'inline-block', width: '24px' }} />
                      b) Realistic Taxable-Bank Counterfactual Component
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      ${Math.round((conversion.conv_tax || 0) - (conversion.tax_drag_cont_pv || 0)).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1">
                      ${Math.round(conversion.hidden_cont_fv || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* Total row — formula reflects netted row 4 (drag alphas in 3a/3b are not double-counted in HC) */}
              <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none' } }}>
                <TableCell sx={{ borderTop: '1px solid #000' }} />
                <TableCell align="right" sx={{ borderTop: '1px solid #000' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    ${Math.round((conversion.spread_pv_tw || 0) + (inputInfo?.tax_payment_source === 'I' ? 0 : conversion.conv_tax) + (conversion.irmaa_savings_pv || 0) + (conversion.tax_drag_rmd_pv || 0))?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ borderTop: '1px solid #000' }}>
                  <Typography variant="body1">
                    ${Math.round(conversion.total_after_tax_dist_chg_amt || 0)?.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" alignItems="center" justifyContent="center" sx={{ border: '2px solid #1976d2', borderRadius: 2, px: 2, py: 0.5, backgroundColor: '#f5f9ff', width: 'fit-content', mx: 'auto', mt: 0.5, mb: 0.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: localTaxSource !== 'I' ? '#0d47a1' : '#999', mr: 0.5 }}>Pay Conversion Tax with Outside (Cash)</Typography>
          <Tooltip title={''} arrow>
            <span>
              <Switch
                size="small"
                checked={localTaxSource === 'I'}
                onChange={() => setTaxSourceConfirmOpen(true)}
              />
            </span>
          </Tooltip>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: localTaxSource === 'I' ? '#0d47a1' : '#999', ml: 0.5 }}>Pay Conversion Tax from Inside Traditional Account</Typography>
        </Box>
        {/* 2026-09-12: Inside payment under 59½ — penalty is folded into conv_tax by the engine.
            Dollar figure is this conversion group's own penalty; zero for groups with no tax. */}
        {localTaxSource === 'I' && (conversion?.conv_penalty_tax || 0) > 0 && (
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#c62828', fontWeight: 'bold', mb: 0.25 }}>
            Conversion tax includes ${Math.round(conversion.conv_penalty_tax).toLocaleString('en-US')} early-withdrawal penalty — affects Rate-Arb &amp; t<sub>C</sub>
          </Typography>
        )}
        {/* 2026-04-19: SS bracket sizing toggle — moved here from inside SS Torpedo accordion for parity with Inside/Outside toggle; same visibility gate as the accordion */}
        {parts && parts.some(p => p.conv_group_num <= (conversion?.conv_group_num || 0) && (p.conv_ss_taxable_delta || 0) > 0) && (
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ border: localUseIRSBrackets === 'Y' ? '2px solid #ff8f00' : '2px solid #1976d2', borderRadius: 2, px: 2, py: 0.5, backgroundColor: localUseIRSBrackets === 'Y' ? '#fff8e1' : '#f5f9ff', width: 'fit-content', mx: 'auto', mt: 0, mb: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: localUseIRSBrackets !== 'Y' ? '#0d47a1' : '#999', mr: 0.5 }}>Conversion + Taxable SS fit IRS Bracket</Typography>
            <Switch
              size="small"
              checked={localUseIRSBrackets === 'Y'}
              onChange={() => setUseIRSBracketsConfirmOpen(true)}
            />
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: localUseIRSBrackets === 'Y' ? '#b71c1c' : '#999', ml: 0.5 }}>Conversion fills IRS Bracket, SS spills to next bracket</Typography>
          </Box>
        )}
      </Box>
      </Box>{/* end combined Summary + Components box */}

      {/* Individual Bracket Conversions Accordion */}
      {parts && parts.length > 0 && (
        <Accordion sx={{ mt: 1, mb: 1, '&.Mui-expanded': { mt: 1, mb: 1 } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e3f2fd', minHeight: 'unset', '&.Mui-expanded': { minHeight: 'unset' }, '& .MuiAccordionSummary-content': { my: 1 }, '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 } }}>
            <Typography variant="subtitle1">
              <strong>BRACKET DETAILS:</strong> This Conversion is comprised of {(() => { const bp = parts.filter(p => p.conv_group_num <= (conversion?.conv_group_num || 0) && p.conv_amt > 0); return `${bp.length} brackets (${bp.map(p => `${(p.tax_rate_bucket * 100).toFixed(0)}%`).join(', ')})`; })()}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* 2026-04-20: overflowY:hidden prevents spec-level overflow-y auto promotion that triggers micro-scroll from subpixel content overflow (see MDN: "if one of overflow-x/y is non-visible and the other is visible, visible is computed as auto") */}
            <TableContainer component={Paper} sx={{ overflowY: 'hidden' }}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5 }, '& tbody td:hover': CELL_HOVER_SPOTLIGHT }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Details</TableCell>
                    <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Wealth Gain (Present Value)</TableCell>
                    <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Post-Conversion — Future Value</TableCell>
                    <TableCell align="center" colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Rates</TableCell>
                  </TableRow>
                  <TableRow sx={{ '& .MuiTableCell-root': { lineHeight: 1.2, py: 0.4 } }}>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}></TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Tax<br />Bracket</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Amount<br />Converted{parts.some(p => (p.conv_ss_taxable_delta || 0) > 0) && <div style={{ fontSize: '0.6rem', color: '#888', lineHeight: 0.8, marginTop: -1, marginBottom: -3 }}>(taxable SS)</div>}</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#c8e6c9', whiteSpace: 'nowrap' }}>{inputInfo?.tax_payment_source === 'I' ? <>Tax Paid<br />from Inside<br />Traditional</> : <>Tax Paid&nbsp;=<br />Hidden Roth<br />Contribution</>}</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd', borderLeft: '3px solid #1976d2' }}>Tax‑Rate<br />Arbitrage</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Medicare<br />Impact</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Tax‑Drag<br />Shelter</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>Net<br />PV<br />(alpha)</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#c8e6c9', borderLeft: '3px solid #1976d2', whiteSpace: 'nowrap' }}>{inputInfo?.tax_payment_source === 'I' ? <>No<br />Hidden<br />Contribution</> : <>Hidden Roth<br />Contribution<br />Growth</>}</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Net<br />FV<br />(alpha)</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Total<br />Spending<br />Increase</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>Return<br />(IRR)</TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd', borderLeft: '3px solid #1976d2' }}>Conversion<br />Tax Rate&nbsp;<strong>t<sub>C</sub></strong></TableCell>
                    <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Distribution<br />Tax Rate&nbsp;<strong>t<sub>D</sub></strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parts
                    .filter(part => part.conv_group_num <= (conversion?.conv_group_num || 0) + 1)
                    .map((part) => {
                      const isIncludedInConversion = part.conv_group_num <= (conversion?.conv_group_num || 0);
                      const isLastIncluded = isIncludedInConversion && part.conv_group_num === (conversion?.conv_group_num || 0);

                      // Determine row style
                      let rowStyle = {};
                      if (part.conv_amt === 0) {
                        rowStyle = { backgroundColor: '#e8e8e8', opacity: 0.6 };
                      } else if (isOutsidePayment) {
                        if (part.conv_irr < portfolioReturn) {
                          rowStyle = { backgroundColor: '#ffebee' };
                        } else if (part.conv_irr > portfolioReturn) {
                          rowStyle = { backgroundColor: '#f1f8e9' };
                        }
                      } else {
                        // Inside: color by PV windfall (spread + irmaa + drag)
                        const partWindfall = (part.spread_pv_tw || 0) + (part.irmaa_savings_pv || 0) + (part.tax_drag_rmd_pv || 0);
                        rowStyle = { backgroundColor: partWindfall >= 0 ? '#f1f8e9' : '#ffebee' };
                      }

                      // Add blue border for included rows
                      if (isIncludedInConversion && part.conv_amt > 0) {
                        rowStyle = {
                          ...rowStyle,
                          borderLeft: '3px solid #1976d2',
                          borderRight: '3px solid #1976d2',
                          ...(part.conv_group_num === parts.find(p => p.conv_amt > 0)?.conv_group_num && { borderTop: '3px solid #1976d2' }),
                          ...(isLastIncluded && { borderBottom: '3px solid #1976d2' }),
                        };
                      }

                      return (
                        <TableRow key={part.conv_group_num} style={rowStyle}>
                          <TableCell align="right">{part.conv_group_num}</TableCell>
                          <TableCell align="right">{(part.tax_rate_bucket * 100).toFixed(1)}%</TableCell>
                          <TableCell align="right">
                            ${part.conv_amt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            {(part.conv_ss_taxable_delta || 0) > 0 && <div style={{ fontSize: '0.6rem', color: '#888', lineHeight: 0.8, marginTop: -2, marginBottom: -4 }}>${Math.round(part.conv_ss_taxable_delta).toLocaleString()} SS</div>}
                          </TableCell>
                          <TableCell align="right">
                            ${part.conv_tax.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>
                            ${Math.round(part.spread_pv_tw || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${Math.round(part.irmaa_savings_pv || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right">
                            ${Math.round((part.tax_drag_rmd_pv || 0) + srcShelterEarnedPv(part)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          {(() => {
                            const partNetPV = part.conversion_alpha_pv || 0;
                            return (
                              <TableCell align="right" style={{ fontWeight: 'bold', color: partNetPV >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                ${Math.round(partNetPV).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                            );
                          })()}
                          <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>
                            ${(Math.round(part.hidden_cont_fv || 0) + 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          {(() => {
                            const partAlphaFV = part.conversion_alpha_fv || 0;
                            return (
                              <TableCell align="right" style={{ color: partAlphaFV >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                ${Math.round(partAlphaFV).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                            );
                          })()}
                          <TableCell align="right">
                            ${Math.round(part.total_after_tax_dist_chg_amt || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                          <TableCell align="right" style={{ fontWeight: 'bold', color: part.conv_irr > portfolioReturn ? '#2e7d32' : part.conv_irr < portfolioReturn ? '#d32f2f' : 'inherit' }}>{(part.conv_irr * 100).toFixed(2)}%</TableCell>
                          <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>{(part.conv_tax_rate * 100).toFixed(1)}%</TableCell>
                          <TableCell align="right">{(part.conv_dist_tax_rate_tw * 100).toFixed(1)}%</TableCell>
                        </TableRow>
                      );
                    })}
                  {/* Blue border explanation */}
                  <TableRow>
                    <TableCell colSpan={14} sx={{ py: 0.5, fontStyle: 'italic', color: 'text.secondary', borderBottom: 'none' }}>
                      Blue border indicates brackets included in this conversion. The next bracket (outside border) shows the marginal impact of converting more.
                    </TableCell>
                  </TableRow>
                  {/* Conversion Summary Row */}
                  {conversion && (
                    <>
                      <TableRow>
                        <TableCell colSpan={14} sx={{ py: 0.5, fontWeight: 'bold', borderBottom: 'none' }}>
                          Conversion Total (sum of brackets inside blue border above):
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{
                        backgroundColor: marginalSpreadPositive ? '#f1f8e9' : '#ffebee'
                      }}>
                        <TableCell align="right">{conversion.conv_group_num}</TableCell>
                        <TableCell align="right">{(conversion.tax_rate_bucket * 100).toFixed(1)}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          ${conversion.conv_amt?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          {(conversion.conv_ss_taxable_delta || 0) > 0 && <div style={{ fontSize: '0.6rem', color: '#888', lineHeight: 0.8, marginTop: -2, marginBottom: -4 }}>${Math.round(conversion.conv_ss_taxable_delta).toLocaleString()} SS</div>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          ${conversion.conv_tax?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>
                          ${Math.round(conversion.spread_pv_tw || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right">
                          ${Math.round(conversion.irmaa_savings_pv || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right">
                          ${Math.round((conversion.tax_drag_rmd_pv || 0) + srcShelterEarnedPv(conversion)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        {(() => {
                          const convNetPV = conversion.conversion_alpha_pv || 0;
                          return (
                            <TableCell align="right" style={{ fontWeight: 'bold', color: convNetPV >= 0 ? '#2e7d32' : '#d32f2f' }}>
                              ${Math.round(convNetPV).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </TableCell>
                          );
                        })()}
                        <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>
                          ${(Math.round(conversion.hidden_cont_fv || 0) + 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        {(() => {
                          const convAlphaFV = conversion.conversion_alpha_fv || 0;
                          return (
                            <TableCell align="right" sx={{ color: convAlphaFV >= 0 ? '#2e7d32' : '#d32f2f' }}>
                              ${Math.round(convAlphaFV).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </TableCell>
                          );
                        })()}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          ${Math.round(conversion.total_after_tax_dist_chg_amt || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right" style={{ fontWeight: 'bold', color: conversion.conv_irr > portfolioReturn ? '#2e7d32' : conversion.conv_irr < portfolioReturn ? '#d32f2f' : 'inherit' }}>{(conversion.conv_irr * 100).toFixed(2)}%</TableCell>
                        <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>{(conversion.conv_tax_rate * 100).toFixed(1)}%</TableCell>
                        <TableCell align="right">{(conversion.conv_dist_tax_rate_tw * 100).toFixed(1)}%</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      {/* SS Torpedo Conversion-Year Cost accordion. 2026-05-05: distribution-years benefit moved to Year-by-Year Detail dropdown */}
      {hasConversionTorpedo && (
        <Accordion sx={{ mt: 1, mb: 1, '&.Mui-expanded': { mt: 1, mb: 1 } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fff3e0', minHeight: 'unset', '&.Mui-expanded': { minHeight: 'unset' }, '& .MuiAccordionSummary-content': { my: 1 }, '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 } }}>
            <Typography variant="subtitle1">
              <Box component="span" sx={{ fontWeight: 'bold' }}>SS TAX TORPEDO DETAILS:</Box> This Conversion Triggers Extra Social Security Taxes This Year!
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
          <Box sx={{ p: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.75, color: '#c62828' }}>
            Conversion Year Cost
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { px: 0.5, py: 0.25 }, '& tbody td:hover': CELL_HOVER_SPOTLIGHT }}>
              <TableHead sx={{ '& .MuiTableCell-root': { py: 0.25, lineHeight: 1.1, borderBottom: '2px solid #000' } }}>
                <TableRow>
                  <TableCell rowSpan={2} align="center" sx={{ verticalAlign: 'bottom' }}>Tax<br/>Bracket</TableCell>
                  <TableCell colSpan={3} align="center" sx={{ borderBottom: '1px solid #999', fontWeight: 'bold' }}>{new Date().getFullYear()} IRS Bracket Amounts</TableCell>
                  <TableCell colSpan={3} align="center" sx={{ borderBottom: '1px solid #999', fontWeight: 'bold' }}>Tax Paid On</TableCell>
                  <TableCell colSpan={3} align="center" sx={{ borderBottom: '1px solid #999', fontWeight: 'bold' }}>Conversion Tax Rate t<sub>C</sub></TableCell>
                </TableRow>
                <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}>
                  <TableCell align="center">Bracket<br/>Size</TableCell>
                  <TableCell align="center">Conversion<br/>Amount</TableCell>
                  <TableCell align="center">Taxable<br/>Soc Sec</TableCell>
                  <TableCell align="center">Conversion<br/>Amount</TableCell>
                  <TableCell align="center">Social<br/>Security</TableCell>
                  <TableCell align="center">Total</TableCell>
                  <TableCell align="center">IRS<br/>Marginal</TableCell>
                  <TableCell align="center">Real<br/>Marginal</TableCell>
                  <TableCell align="center">{inputInfo?.use_irs_brackets === 'Y' ? <>SS-Adjusted<br/>| Actual</> : <>Rate<br/>Multiplier</>}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parts.filter(p => p.conv_group_num <= (conversion?.conv_group_num || 0) && p.conv_amt > 0).map(p => {
                  // 2026-04-18: under use_irs_brackets='Y', conv_amt already equals bracket width (SS spills out); only sum them under 'N' where they fit within the bracket
                  const bracketSize = inputInfo?.use_irs_brackets === 'Y' ? (p.conv_amt || 0) : ((p.conv_amt || 0) + (p.conv_ss_taxable_delta || 0));
                  const ssTax = p.conv_ss_tax_delta || 0;
                  const groupTax = p.conv_tax || 0;
                  const tC = p.conv_tax_rate || 0;
                  const bracket = p.tax_rate_bucket || 0;
                  const tcRatio = bracket > 0 ? (tC / bracket) : 0;
                  return (
                    <TableRow key={p.conv_group_num}>
                      <TableCell align="center">{(bracket * 100).toFixed(0)}%</TableCell>
                      <TableCell align="right">${Math.round(bracketSize).toLocaleString()}</TableCell>
                      <TableCell align="right">${Math.round(p.conv_amt).toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: (p.conv_ss_taxable_delta || 0) > 0 ? 'error.main' : 'inherit' }}>
                        ${Math.round(p.conv_ss_taxable_delta || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">${Math.round(groupTax - ssTax).toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: ssTax > 0 ? 'error.main' : 'inherit' }}>
                        ${Math.round(ssTax).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">${Math.round(groupTax).toLocaleString()}</TableCell>
                      <TableCell align="center">{(bracket * 100).toFixed(0)}%</TableCell>
                      <TableCell align="right">{(tC * 100).toFixed(1)}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: tcRatio > 1.3 ? 'error.main' : 'inherit' }}>
                        {/* 2026-04-19: under use_irs_brackets='Y', show "ghost_free | actual" (ghost_free = multiplier if SS stayed within bracket rate) */}
                        {(() => {
                          const ghostFree = (p.conv_amt || 0) > 0 ? (1 + (p.conv_ss_taxable_delta || 0) / p.conv_amt) : 0;
                          const actual = bracket > 0 ? tcRatio : ghostFree;
                          if (inputInfo?.use_irs_brackets === 'Y') {
                            return (p.conv_amt || 0) > 0 ? `${ghostFree.toFixed(2)}x | ${actual.toFixed(2)}x` : '\u2014';
                          }
                          return bracket > 0 ? `${tcRatio.toFixed(2)}x` : ((p.conv_amt || 0) > 0 && (p.conv_ss_taxable_delta || 0) > 0 ? `${ghostFree.toFixed(2)}x` : '\u2014');
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                {(() => {
                  const bp = parts.filter(p => p.conv_group_num <= (conversion?.conv_group_num || 0) && p.conv_amt > 0);
                  // 2026-04-18: see line 1299 comment — same conditional for totals row
                  const totalSize = bp.reduce((s, p) => s + (p.conv_amt || 0) + (inputInfo?.use_irs_brackets === 'Y' ? 0 : (p.conv_ss_taxable_delta || 0)), 0);
                  const totalConv = bp.reduce((s, p) => s + (p.conv_amt || 0), 0);
                  const totalSS = bp.reduce((s, p) => s + (p.conv_ss_taxable_delta || 0), 0);
                  const totalSSTax = bp.reduce((s, p) => s + (p.conv_ss_tax_delta || 0), 0);
                  const totalTax = bp.reduce((s, p) => s + (p.conv_tax || 0), 0);
                  const totalTC = conversion?.conv_tax_rate || 0;
                  // 2026-04-18: see line 1299 comment — same conditional for weighted-avg bracket
                  const wtdAvgBracket = totalSize > 0 ? bp.reduce((s, p) => s + (p.tax_rate_bucket || 0) * ((p.conv_amt || 0) + (inputInfo?.use_irs_brackets === 'Y' ? 0 : (p.conv_ss_taxable_delta || 0))), 0) / totalSize : 0;
                  return (
                    <TableRow sx={{ '& .MuiTableCell-root': { borderTop: '2px solid #000', fontWeight: 'bold', py: 0.5 } }}>
                      <TableCell>Total</TableCell>
                      <TableCell align="right">${Math.round(totalSize).toLocaleString()}</TableCell>
                      <TableCell align="right">${Math.round(totalConv).toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>${Math.round(totalSS).toLocaleString()}</TableCell>
                      <TableCell align="right">${Math.round(totalTax - totalSSTax).toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>${Math.round(totalSSTax).toLocaleString()}</TableCell>
                      <TableCell align="right">${Math.round(totalTax).toLocaleString()}</TableCell>
                      <TableCell align="center">{(wtdAvgBracket * 100).toFixed(1)}%</TableCell>
                      <TableCell align="right">{(totalTC * 100).toFixed(1)}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{wtdAvgBracket > 0 ? `${(totalTC / wtdAvgBracket).toFixed(2)}x` : '\u2014'}</TableCell>
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
          {/* SS Torpedo explanation text and yearly detail */}
          {(() => {
            const bp = parts.filter(p => p.conv_group_num <= (conversion?.conv_group_num || 0) && p.conv_amt > 0 && (p.conv_ss_taxable_delta || 0) > 0);
            if (bp.length === 0) return null;
            // Find a bracket where SS torpedo is most visible (highest multiplier with tax > 0)
            const example = bp.filter(p => p.tax_rate_bucket > 0).sort((a, b) => {
              const multA = a.conv_tax_rate / a.tax_rate_bucket;
              const multB = b.conv_tax_rate / b.tax_rate_bucket;
              return multB - multA;
            })[0];
            if (!example) return null;
            const bracketPct = (example.tax_rate_bucket * 100).toFixed(0);
            const bracketSize = Math.round((example.conv_amt || 0) + (example.conv_ss_taxable_delta || 0));
            const convAmt = Math.round(example.conv_amt);
            const ssAmt = Math.round(example.conv_ss_taxable_delta || 0);
            const realRate = (example.conv_tax_rate * 100).toFixed(1);
            const multiplier = (example.conv_tax_rate / example.tax_rate_bucket).toFixed(2);
            const ssCap = Math.round((conversion?.conv_ss_taxable_delta || 0));
            // 2026-04-19: text is mode-aware — 'N' (SS-adjusted) describes why brackets are reduced; 'Y' (full brackets) describes the spillover consequence using the user's aggregate conversion selection plus a concrete bracket example
            if (inputInfo?.use_irs_brackets === 'Y') {
              // Aggregate figures for the user's selected conversion
              const topBracketPart = parts.filter(p => p.conv_group_num <= (conversion?.conv_group_num || 0) && p.conv_amt > 0).sort((a, b) => (b.tax_rate_bucket || 0) - (a.tax_rate_bucket || 0))[0];
              const topBracketPct = topBracketPart ? (topBracketPart.tax_rate_bucket * 100).toFixed(0) : '—';
              const totalConvAmt = Math.round(conversion?.conv_amt || 0);
              const totalSSTriggered = Math.round(conversion?.conv_ss_taxable_delta || 0);
              const aggRealRate = ((conversion?.conv_tax_rate || 0) * 100).toFixed(1);
              // 2026-04-20: determine the specific bracket where SS spillover lands (the next rate above topBracketPct)
              const allRatesForSpillover = [...new Set(parts.map(p => Number(p.tax_rate_bucket || 0)))].sort((a, b) => a - b);
              const topRate = Number(topBracketPart?.tax_rate_bucket || 0);
              const spilloverRate = allRatesForSpillover.filter(r => r > topRate)[0];
              const spilloverPct = spilloverRate ? `${(spilloverRate * 100).toFixed(0)}%` : 'the next bracket';
              // Concrete example: pick bracket with highest SS triggered (cleanest illustration of spillover)
              const exampleRow = bp.filter(p => p.tax_rate_bucket > 0).sort((a, b) => (b.conv_ss_taxable_delta || 0) - (a.conv_ss_taxable_delta || 0))[0];
              let exampleParagraph = null;
              if (exampleRow) {
                const examplePct = (exampleRow.tax_rate_bucket * 100).toFixed(0);
                const bracketWidth = Number(exampleRow.conv_amt);
                const exampleConv = Math.round(bracketWidth);
                const exampleSS = Math.round(exampleRow.conv_ss_taxable_delta || 0);
                const agiMult = (1 + (exampleRow.conv_ss_taxable_delta || 0) / (bracketWidth || 1)).toFixed(2);
                const actualMult = ((exampleRow.conv_tax_rate || 0) / (exampleRow.tax_rate_bucket || 1)).toFixed(2);
                // Cumulative prior taxable SS sitting in the stack before this bracket's group
                const priorSS = parts.filter(p => p.conv_group_num < exampleRow.conv_group_num && p.conv_amt > 0)
                  .reduce((sum, p) => sum + Number(p.conv_ss_taxable_delta || 0), 0);
                const availableAtRate = Math.max(0, bracketWidth - priorSS);
                const convSpilling = Math.round(bracketWidth - availableAtRate);
                const convAtRate = Math.round(availableAtRate);
                const priorSSDisp = Math.round(priorSS);
                // Next bracket rate for spillover destination
                const allRates = [...new Set(parts.map(p => Number(p.tax_rate_bucket || 0)))].sort((a, b) => a - b);
                const nextRate = allRates.filter(r => r > Number(exampleRow.tax_rate_bucket))[0] || null;
                const nextPct = nextRate ? `${(nextRate * 100).toFixed(0)}%` : 'the next bracket';
                if (priorSS > 0 && convSpilling > 0) {
                  // Full ghost-displacement case: prior SS consumed bracket space, causing BOTH conversion and new SS to spill
                  exampleParagraph = (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>For example</strong>, in the {examplePct}% bracket: converting <strong>${exampleConv.toLocaleString()}</strong> triggers <strong>${exampleSS.toLocaleString()}</strong> of taxable SS (each conversion dollar adds up to $0.85 of AGI — a <strong>{agiMult}×</strong> AGI multiple). But <strong>${priorSSDisp.toLocaleString()}</strong> of taxable SS from prior brackets already sits in the {examplePct}% bracket space, leaving only <strong>${convAtRate.toLocaleString()}</strong> of your conversion at the {examplePct}% rate. The remaining <strong>${convSpilling.toLocaleString()}</strong> of conversion AND all <strong>${exampleSS.toLocaleString()}</strong> of new SS spill into <strong>{nextPct}</strong> — producing the <strong>{actualMult}×</strong> real tax-rate multiplier for this row in the table above.
                    </Typography>
                  );
                } else {
                  // Simple case: no ghost displacement (typically the first SS-triggering bracket, e.g. 0%/std ded)
                  exampleParagraph = (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>For example</strong>, in the {examplePct}% bracket: converting <strong>${exampleConv.toLocaleString()}</strong> triggers <strong>${exampleSS.toLocaleString()}</strong> of taxable SS (each conversion dollar adds up to $0.85 of AGI — a <strong>{agiMult}×</strong> AGI multiple). Because the conversion alone fills the {examplePct}% bracket, that SS gets taxed at <strong>{nextPct}</strong> (the next bracket's rate) — producing the <strong>{actualMult}×</strong> real tax-rate multiplier for this row in the table above.
                    </Typography>
                  );
                }
              }
              return (
                <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#fff8e1', borderLeft: '3px solid #ff8f00', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Full-Bracket Mode: Your <strong>${totalConvAmt.toLocaleString()}</strong> conversion fills IRS brackets up through <strong>{topBracketPct}%</strong>, triggering <strong>${totalSSTriggered.toLocaleString()}</strong> of taxable Social Security that spills into the <strong>{spilloverPct}</strong> bracket. Your aggregate real conversion tax rate is <strong>{aggRealRate}%</strong> — see each bracket's multiplier in the table above.
                  </Typography>
                  {exampleParagraph}
                  {ssCap > 0 && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Once taxable Social Security reaches its 85% maximum (<strong>${ssCap.toLocaleString()}</strong>), the maxed SS sits atop your income stack and keeps pushing <em>subsequent</em> bracket conversions up into the next higher rate — "ghost displacement" that continues even in rows where no new SS is triggered. The <strong>SS-Adjusted | Actual</strong> column shows this side by side: the left number is the clean multiplier; the right is what ghost displacement costs you.
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Toggle to <em>'Conversion + Taxable SS fit IRS Bracket'</em> above to size each conversion so the conversion <strong>plus</strong> its triggered SS together stay within the bracket — capturing the full tax-rate spread without paying next-bracket rates on the SS.
                  </Typography>
                </Box>
              );
            }
            return (
              // 2026-04-19: 'N' mode — informational tone, light blue (vs. 'Y' mode's cautionary yellow/orange); numbers bolded to match 'Y' style
              <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#e3f2fd', borderLeft: '3px solid #1976d2', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  The IRS <strong>{bracketPct}%</strong> bracket is <strong>${bracketSize.toLocaleString()}</strong> wide, but only <strong>${convAmt.toLocaleString()}</strong> is available for conversion — the remaining <strong>${ssAmt.toLocaleString()}</strong> is taxable Social Security.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Each converted dollar triggers up to <strong>$0.85</strong> of taxable Social Security — the IRS Provisional Income formula.
                  The conversion, plus the triggered taxable Social Security, fills the bracket, leaving less convertible dollars.
                  In the {bracketPct}% bracket, <strong>${ssAmt.toLocaleString()}</strong> of the <strong>${bracketSize.toLocaleString()}</strong> is consumed by taxable Social Security,
                  leaving <strong>${convAmt.toLocaleString()}</strong> for conversion. The real conversion tax rate is <strong>{realRate}%</strong> (<strong>{multiplier}×</strong> the posted {bracketPct}% bracket).
                </Typography>
                {ssCap > 0 && (() => {
                  // Find the bracket where SS caps (last bracket with multiplier > 1)
                  const cappedBracket = [...bp].reverse().find(p => p.tax_rate_bucket > 0 && (p.conv_tax_rate / p.tax_rate_bucket) > 1.01 && (p.conv_tax_rate / p.tax_rate_bucket) < 1.84);
                  return (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Once taxable Social Security reaches the <strong>85%</strong> maximum, <strong>${ssCap.toLocaleString()}</strong>, additional conversion dollars are taxed at the posted bracket rate —
                      and subsequent brackets are filled by conversion dollars alone.
                      {cappedBracket && <> Social Security is maxed out in the <strong>{(cappedBracket.tax_rate_bucket * 100).toFixed(0)}%</strong> bracket, as the <strong>{(cappedBracket.conv_tax_rate / cappedBracket.tax_rate_bucket).toFixed(2)}x</strong> rate multiplier shows.</>}
                    </Typography>
                  );
                })()}
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Many investors and advisors strategically convert to fill a partficular IRS bracket, potentially ignoring the taxable Social Security that conversion triggers.
                  This pushes taxable Social Security income into the next higher-rate bracket, further amplifying the tax impact. Sizing each conversion to account for Social Security 
                  taxation means you never accidentally convert into a higher bracket than intended.
                </Typography>
              </Box>
            );
          })()}
          </Box>
          {/* 2026-05-05: Distribution Years Benefit table moved to Year-by-Year Detail dropdown as 'Social Security Tax Reduction' */}
          </AccordionDetails>
        </Accordion>
      )}

      {/* COMPARE Inside vs Outside accordion moved to ConceptsTab 2026-05-04 */}

      {/* Buttons at bottom */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {onAddAssumptions && (
          <Button variant="contained" color="primary" onClick={onAddAssumptions}>
            Edit Assumptions & Re-Run
          </Button>
        )}
        {!isRecommended && onViewRecommended && (
          recommendedConversion ? (
            <Button variant="contained" color="success" onClick={onViewRecommended}>
              Show Recommended Conversion
            </Button>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main', alignSelf: 'center' }}>
              No profitable conversion at these assumptions
            </Typography>
          )
        )}
        <Box sx={{ flexGrow: 1 }} />
        {runId && (
          <>
            <Button
              variant="outlined"
              sx={{ fontWeight: 'bold', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
              onClick={async () => {
                try {
                  const blob = await apiService.downloadReport(runId);
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `roth_report_${runId}.docx`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  let detail = `HTTP ${err.response?.status || '?'}`;
                  if (err.response?.data instanceof Blob) {
                    try {
                      const text = await err.response.data.text();
                      detail = JSON.parse(text).detail || text || detail;
                    } catch (_) {}
                  } else if (err.response?.data?.detail) {
                    detail = err.response.data.detail;
                  }
                  alert(`Failed to download report: ${detail}`);
                }
              }}
            >
              Download Report
            </Button>
            <Button
              variant="outlined"
              sx={{ fontWeight: 'bold', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
              onClick={async () => {
                try {
                  await apiService.emailReport(runId);
                  alert('Report emailed successfully!');
                } catch (err) {
                  alert(err.response?.data?.detail || 'Failed to email report');
                }
              }}
            >
              Email Report
            </Button>
          </>
        )}
      </Box>

      {/* Tax Payment Source Confirm Dialog */}
      <Dialog open={taxSourceConfirmOpen} onClose={() => setTaxSourceConfirmOpen(false)}>
        <DialogTitle>Change Conversion Tax Payment Source?</DialogTitle>
        <DialogContent>
          <Typography>
            Switch to <strong>{pendingTaxSource === 'I' ? 'Inside Traditional Account' : 'Outside (Cash)'}</strong> & re-run calculations?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxSourceConfirmOpen(false)}>Cancel</Button>
          <Button onClick={async () => { setLocalTaxSource(pendingTaxSource); setTaxSourceConfirmOpen(false); await onToggleTaxSource(pendingTaxSource); }} color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* 2026-04-18: SS Torpedo Bracket Sizing Confirm Dialog */}
      <Dialog open={useIRSBracketsConfirmOpen} onClose={() => setUseIRSBracketsConfirmOpen(false)}>
        <DialogTitle>Change Conversion Bracket Sizing?</DialogTitle>
        <DialogContent>
          <Typography>
            Switch to <strong>{pendingUseIRSBrackets === 'Y' ? 'Full IRS Brackets (allow SS to spill into next bracket)' : 'SS-Torpedo Adjusted (RothGPT default)'}</strong> & re-run calculations?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseIRSBracketsConfirmOpen(false)}>Cancel</Button>
          <Button onClick={async () => { setLocalUseIRSBrackets(pendingUseIRSBrackets); setUseIRSBracketsConfirmOpen(false); await onToggleUseIRSBrackets(pendingUseIRSBrackets); }} color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecommendationTab;
